import React, { useState, useEffect } from 'react';
import type { Slide } from '@/framework/types';
import './design-slides.css';
import titleBg from './images/title-bg.png';
import s1Shape0 from './images/s1_shape0.png';
import s1Shape1 from './images/s1_shape1.png';
import agentConsumer from './images/agent-consumer.jpg';

/* ══════════════════════════════════════════════════════════════════════
   Shared inline style objects
   ══════════════════════════════════════════════════════════════════════ */

const S = {
  /* ── Layout helpers ── */
  flexRow: { display: 'flex', gap: '2rem' } as React.CSSProperties,
  flexCol: { display: 'flex', flexDirection: 'column' as const, gap: '1rem' } as React.CSSProperties,
  twoCol: { display: 'flex', gap: '3rem', flex: 1 } as React.CSSProperties,
  threeCol: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem' } as React.CSSProperties,
  fourCol: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' } as React.CSSProperties,
  fourGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', flex: 1 } as React.CSSProperties,

  /* ── Typography ── */
  h1: { fontFamily: '"Funnel Display", sans-serif', fontSize: '42px', fontWeight: 700, lineHeight: 1.15, margin: 0 } as React.CSSProperties,
  h2: { fontFamily: '"Funnel Display", sans-serif', fontSize: '42px', fontWeight: 700, lineHeight: 1.2, margin: '0 0 0.75rem' } as React.CSSProperties,
  h3: { fontFamily: '"Funnel Display", sans-serif', fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.3, margin: '0 0 0.5rem' } as React.CSSProperties,
  body: { fontSize: '1.05rem', lineHeight: 1.6, margin: 0, opacity: 0.92 } as React.CSSProperties,
  muted: { fontSize: '0.85rem', opacity: 0.55, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.08em' } as React.CSSProperties,
  label: { fontSize: '0.75rem', opacity: 0.5, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', margin: '0 0 0.25rem' } as React.CSSProperties,
  accent: { color: '#0070D9' } as React.CSSProperties,
  white: { color: '#fff' } as React.CSSProperties,
  lightGray: { color: 'rgba(255,255,255,0.7)' } as React.CSSProperties,
  darkText: { color: '#1a1a2e' } as React.CSSProperties,

  /* ── Cards ── */
  card: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '1.5rem',
  } as React.CSSProperties,

  /* ── Code ── */
  code: {
    background: '#1e2030',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: '0.78rem',
    lineHeight: 1.55,
    overflowX: 'auto' as const,
    color: '#e0e0e0',
    margin: '0.5rem 0 0',
    whiteSpace: 'pre' as const,
  } as React.CSSProperties,

  /* ── Placeholder image ── */
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: '400px',
    background: 'linear-gradient(135deg, #f0f0f0, #e0e0e0)',
    borderRadius: '8px',
    fontSize: '1.25rem',
    color: '#999',
    fontWeight: 600,
  } as React.CSSProperties,

  /* ── Section divider ── */
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /* ── Footer bar ── */
  footer: {
    marginTop: 'auto',
    padding: '0.75rem 1.25rem',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '8px',
    fontSize: '0.85rem',
    opacity: 0.7,
  } as React.CSSProperties,

  /* ── Numbered list ── */
  numberedItem: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    marginBottom: '1rem',
  } as React.CSSProperties,
  numberCircle: {
    width: '2rem',
    height: '2rem',
    minWidth: '2rem',
    borderRadius: '50%',
    background: '#0070D9',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.9rem',
  } as React.CSSProperties,

  /* ── Flow / diagram containers ── */
  flowBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    padding: '1.25rem',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.08)',
  } as React.CSSProperties,
  arrow: { fontSize: '1.5rem', opacity: 0.5 } as React.CSSProperties,

  /* ── Tag / badge ── */
  tag: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    background: 'rgba(24,143,255,0.15)',
    color: '#0070D9',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  tagGreen: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    background: 'rgba(52,211,153,0.15)',
    color: '#34d399',
  } as React.CSSProperties,

  tagOrange: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    background: 'rgba(251,191,36,0.15)',
    color: '#fbbf24',
  } as React.CSSProperties,

  tagRed: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    background: 'rgba(239,68,68,0.15)',
    color: '#ef4444',
  } as React.CSSProperties,
} as const;

/* ══════════════════════════════════════════════════════════════════════
   Slide  1 — Title
   ══════════════════════════════════════════════════════════════════════ */

const Slide01 = () => (
  <div className="slide-body" style={{
    position: 'relative',
    backgroundImage: `url(${titleBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: 0,
    overflow: 'hidden',
  }}>
    {/* Dots / logo pattern — right side */}
    <img src={s1Shape0} alt="" style={{
      position: 'absolute',
      right: '2%',
      top: '50%',
      transform: 'translateY(-50%)',
      height: '80%',
      objectFit: 'contain',
      pointerEvents: 'none',
    }} />
    {/* StreamNative logo — top left */}
    <img src={s1Shape1} alt="StreamNative" style={{
      position: 'absolute',
      top: '5%',
      left: '3%',
      height: '42px',
      objectFit: 'contain',
    }} />
    {/* Text content — left side */}
    <div style={{
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'flex-start',
      height: '100%',
      padding: '3rem 4rem',
      maxWidth: '60%',
    }}>
      <div style={{ ...S.muted, color: 'rgba(255,255,255,0.7)' }}>MAY 2026</div>
      <h1 style={{ ...S.h1, fontSize: '88px', marginTop: '0.5rem', color: '#fff' }}>
        From Data Pipelines<br />to Context Streams
      </h1>
      <p style={{ ...S.body, fontSize: '23px', opacity: 0.7, marginTop: '0.5rem', color: '#fff' }}>
        Building Infrastructure for the Agent Era
      </p>
      <div style={{ marginTop: '2rem', fontSize: '1.05rem', opacity: 0.8, color: '#fff' }}>
        David Kjerrumgaard
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  2 — A New Primary Consumer
   ══════════════════════════════════════════════════════════════════════ */

const Slide02 = () => (
  <div className="slide-body" style={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto 1fr',
    gap: '1.25rem 1.5rem',
    position: 'absolute' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    padding: '90px 110px 80px',
    maxWidth: 'none',
    margin: 0,
  }}>
    {/* Title — spans only left column */}
    <h2 style={{ ...S.h2, margin: 0, color: '#fff', whiteSpace: 'nowrap' as const }}>A New Primary Consumer</h2>
    {/* Empty cell above image */}
    <div />
    {/* Left panel — body text */}
    <div style={{
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1.25rem',
      justifyContent: 'flex-start',
    }}>
      <p style={{ ...S.body, fontSize: '23px', color: 'rgba(255,255,255,0.85)' }}>
        For thirty years we built data infrastructure for human consumers &mdash; analysts,
        scientists, executives. They are forgiving customers.
      </p>
      <p style={{ ...S.body, fontSize: '23px', color: 'rgba(255,255,255,0.85)' }}>
        We have a new primary consumer now: <em style={{ color: '#62C6E8' }}>AI agents</em>. They are not
        forgiving. They don&apos;t wait for nightly batches, they don&apos;t tolerate stale data,
        and they don&apos;t shrug at bad inputs &mdash; they <em>hallucinate</em>.
      </p>
      <p style={{ ...S.body, fontSize: '23px', color: 'rgba(255,255,255,0.85)' }}>
        In agentic systems, the <em style={{ fontWeight: 700, fontStyle: 'italic' }}>context window</em> is the source of truth.
      </p>
    </div>
    {/* Right panel — image, top-aligned with body text */}
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
    }}>
      <img src={agentConsumer} alt="AI agent consuming data" style={{ width: '100%', maxHeight: '100%', objectFit: 'contain' as const, borderRadius: '10px' }} />
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  3 — Section Divider
   ══════════════════════════════════════════════════════════════════════ */

const comparisonRows = [
  { dimension: 'Latency', human: 'Waits 12s for a query', agent: 'Times out at 500ms inside a tool call' },
  { dimension: 'Staleness', human: 'Tolerates 30-min lag', agent: 'Acts on stale data → wrong tool call' },
  { dimension: 'Consumption', human: 'Reads dashboards on demand', agent: 'Needs context assembled on event arrival' },
  { dimension: 'Reasoning', human: 'Reasons over columns', agent: 'Reasons over context windows', bold: true },
  { dimension: 'Error handling', human: 'Catches errors visually', agent: 'Propagates errors to downstream agents' },
  { dimension: 'Failure cost', human: 'Re-runs the query', agent: 'Cascading failures across agent mesh' },
] as const;

const tableCell = {
  padding: '18px 24px',
  fontSize: '22px',
  lineHeight: 1.5,
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  verticalAlign: 'middle' as const,
} as React.CSSProperties;

const Slide03 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.5rem' }}>
    <h1 style={S.h1}>
      Current Data Infrastructure is Built for <em style={S.accent}>Humans</em>, Not Agents
    </h1>
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
      <thead>
        <tr>
          <th style={{ ...tableCell, background: '#082B3D', color: '#fff', fontWeight: 700, fontSize: '24px', width: '20%', borderBottom: 'none' }}>&nbsp;</th>
          <th style={{ ...tableCell, background: '#082B3D', color: '#fff', fontWeight: 700, fontSize: '24px', width: '40%', borderBottom: 'none' }}>Human Analyst</th>
          <th style={{ ...tableCell, background: '#082B3D', color: '#fff', fontWeight: 700, fontSize: '24px', width: '40%', borderBottom: 'none' }}>AI Agent</th>
        </tr>
      </thead>
      <tbody>
        {comparisonRows.map((row, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
            <td style={{ ...tableCell, background: '#E5F1F7', color: '#0F86C7', fontWeight: 700, fontSize: '22px' }}>{row.dimension}</td>
            <td style={tableCell}>{row.human}</td>
            <td style={tableCell}>
              {'bold' in row && row.bold
                ? <>Reasons over <strong style={{ fontWeight: 700, fontStyle: 'italic' }}>context windows</strong></>
                : row.agent}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  4 — What Do Agents Need?
   ══════════════════════════════════════════════════════════════════════ */

const Slide04 = () => (
  <div className="slide-body design-slide">
    <div className="frame s1">
      <div className="s1-left">
        <h1 className="title">What do agents <em>need?</em></h1>

        <div className="answer-row">
          <span className="answer-label">The answer</span>
          <span className="answer-word"><span className="underline">Context.</span></span>
        </div>

        <div className="body-text body-stack">
          <p>Think of it as the AI{'\''}s <strong>short-term memory capacity</strong>.</p>
          <p>When you start a conversation with an agent, both the <strong>input</strong> — your questions, instructions, and retrieved data — and the <strong>output</strong> — the agent{'\''}s response — live inside the context window of whichever model you{'\''}ve chosen.</p>
        </div>
      </div>

      <div className="head-stage">
        <div className="head-caption">Empty context · 0 tokens used</div>
        <svg className="robot-head" viewBox="0 0 540 560" xmlns="http://www.w3.org/2000/svg" aria-label="Empty robot head">
          <defs>
            <linearGradient id="headFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0E455E" />
              <stop offset="100%" stopColor="#082B3D" />
            </linearGradient>
            <linearGradient id="visor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0F86C7" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#0F86C7" stopOpacity="0.06" />
            </linearGradient>
          </defs>

          {/* antenna */}
          <line x1="270" y1="8" x2="270" y2="48" stroke="#62C6E8" strokeWidth="3" strokeLinecap="round" />
          <circle cx="270" cy="8" r="7" fill="#62C6E8" />

          {/* neck */}
          <rect x="232" y="470" width="76" height="42" rx="10" fill="#1A4156" stroke="#62C6E8" strokeWidth="1.5" />
          <line x1="246" y1="486" x2="294" y2="486" stroke="#62C6E8" strokeOpacity="0.5" strokeWidth="1" />
          <line x1="246" y1="498" x2="294" y2="498" stroke="#62C6E8" strokeOpacity="0.5" strokeWidth="1" />

          {/* head outer */}
          <path d="M120 230 C 120 110, 195 50, 270 50 C 345 50, 420 110, 420 230 L 420 380 C 420 440, 370 480, 310 480 L 230 480 C 170 480, 120 440, 120 380 Z" fill="url(#headFill)" stroke="#62C6E8" strokeWidth="2" />

          {/* inner panel divisions (faint) */}
          <path d="M120 230 L 420 230" stroke="#62C6E8" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M150 380 L 390 380" stroke="#62C6E8" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M270 230 L 270 380" stroke="#62C6E8" strokeOpacity="0.18" strokeWidth="1" strokeDasharray="4 6" />

          {/* side ear panels */}
          <rect x="100" y="270" width="22" height="70" rx="5" fill="#1A4156" stroke="#62C6E8" strokeWidth="1.2" />
          <rect x="418" y="270" width="22" height="70" rx="5" fill="#1A4156" stroke="#62C6E8" strokeWidth="1.2" />
          <circle cx="111" cy="290" r="3" fill="#62C6E8" />
          <circle cx="111" cy="305" r="3" fill="#62C6E8" fillOpacity="0.4" />
          <circle cx="429" cy="290" r="3" fill="#62C6E8" />
          <circle cx="429" cy="305" r="3" fill="#62C6E8" fillOpacity="0.4" />

          {/* visor */}
          <rect x="160" y="240" width="220" height="100" rx="14" fill="url(#visor)" stroke="#62C6E8" strokeWidth="1.5" />
          <rect x="160" y="240" width="220" height="100" rx="14" fill="none" stroke="#62C6E8" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="3 5" />

          {/* visor empty label */}
          <text x="270" y="296" textAnchor="middle" fontFamily="DM Mono, monospace" fontSize="22" letterSpacing="6" fill="#62C6E8" fillOpacity="0.55">EMPTY</text>

          {/* jaw / mouth grille */}
          <rect x="210" y="410" width="120" height="22" rx="4" fill="#0E455E" stroke="#62C6E8" strokeWidth="1.2" />
          <line x1="222" y1="410" x2="222" y2="432" stroke="#62C6E8" strokeOpacity="0.4" />
          <line x1="240" y1="410" x2="240" y2="432" stroke="#62C6E8" strokeOpacity="0.4" />
          <line x1="258" y1="410" x2="258" y2="432" stroke="#62C6E8" strokeOpacity="0.4" />
          <line x1="276" y1="410" x2="276" y2="432" stroke="#62C6E8" strokeOpacity="0.4" />
          <line x1="294" y1="410" x2="294" y2="432" stroke="#62C6E8" strokeOpacity="0.4" />
          <line x1="312" y1="410" x2="312" y2="432" stroke="#62C6E8" strokeOpacity="0.4" />

          {/* crown bolts */}
          <circle cx="170" cy="120" r="5" fill="#62C6E8" fillOpacity="0.55" />
          <circle cx="370" cy="120" r="5" fill="#62C6E8" fillOpacity="0.55" />

          {/* subtle shine */}
          <path d="M180 80 Q 220 60 270 60" stroke="#62C6E8" strokeOpacity="0.35" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  5 — Tokens
   ══════════════════════════════════════════════════════════════════════ */

const Slide05 = () => (
  <div className="slide-body design-slide">
    <div className="frame s2">
      <div className="s2-left">
        <h1 className="title">What goes inside a context?<br /><em>Tokens.</em></h1>

        <div className="body-text" style={{ maxWidth: 640 }}>
          Every prompt, document, tool call and response is broken into tokens — short fragments of characters — before it ever enters the model.
        </div>

        <div className="text-source">
          <span className="src-label">Raw text</span>
          {'"'}Build infrastructure for the agent era.{'"'}
        </div>

        <div className="arrow-decompose">
          <span>Tokenize</span>
          <span className="line" />
        </div>

        <div className="token-stream">
          <span className="tok t-1">{'"'}Build</span>
          <span className="tok t-2">{'"'}&nbsp;infra</span>
          <span className="tok t-3">structure</span>
          <span className="tok t-4">{'"'}&nbsp;for</span>
          <span className="tok t-5">{'"'}&nbsp;the</span>
          <span className="tok t-6">{'"'}&nbsp;agent</span>
          <span className="tok t-7">{'"'}&nbsp;era</span>
          <span className="tok t-1">.{'"'}</span>
        </div>
      </div>
      <div className="head-fill-stage">
        <div className="meter">
          <span className="meter-label">Context fill</span>
          <div className="meter-bar"><div className="meter-fill" /></div>
          <span className="meter-num">~ 124 / 200 tokens</span>
        </div>

        <svg className="head-with-tokens" viewBox="0 0 540 560" xmlns="http://www.w3.org/2000/svg" aria-label="Robot head filling with tokens">
          <defs>
            <linearGradient id="headFill2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0E455E" />
              <stop offset="100%" stopColor="#082B3D" />
            </linearGradient>
            <clipPath id="headClip">
              <path d="M120 230 C 120 110, 195 50, 270 50 C 345 50, 420 110, 420 230 L 420 380 C 420 440, 370 480, 310 480 L 230 480 C 170 480, 120 440, 120 380 Z" />
            </clipPath>
          </defs>

          {/* antenna */}
          <line x1="270" y1="8" x2="270" y2="48" stroke="#62C6E8" strokeWidth="3" strokeLinecap="round" />
          <circle cx="270" cy="8" r="7" fill="#62C6E8" />

          {/* neck */}
          <rect x="232" y="470" width="76" height="42" rx="10" fill="#1A4156" stroke="#62C6E8" strokeWidth="1.5" />
          <line x1="246" y1="486" x2="294" y2="486" stroke="#62C6E8" strokeOpacity="0.5" strokeWidth="1" />
          <line x1="246" y1="498" x2="294" y2="498" stroke="#62C6E8" strokeOpacity="0.5" strokeWidth="1" />

          {/* head outer */}
          <path d="M120 230 C 120 110, 195 50, 270 50 C 345 50, 420 110, 420 230 L 420 380 C 420 440, 370 480, 310 480 L 230 480 C 170 480, 120 440, 120 380 Z" fill="url(#headFill2)" stroke="#62C6E8" strokeWidth="2" />

          {/* TOKENS FILLING THE BRAIN */}
          <g clipPath="url(#headClip)">
            <rect x="100" y="60" width="340" height="170" fill="#0F86C7" fillOpacity="0.05" />
            <g fontFamily="DM Mono, monospace" fontSize="8">
              <rect x="258" y="72" width="24" height="12" rx="3" fill="#FCE3A6" /><text x="270" y="81" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="232" y="88" width="22" height="12" rx="3" fill="#C8E5DA" /><text x="243" y="97" textAnchor="middle" fill="#082B3D">the</text>
              <rect x="258" y="88" width="30" height="12" rx="3" fill="#F6D4C9" /><text x="273" y="97" textAnchor="middle" fill="#082B3D">agent</text>
              <rect x="292" y="88" width="22" height="12" rx="3" fill="#DDD3EE" /><text x="303" y="97" textAnchor="middle" fill="#082B3D">era</text>
              <rect x="206" y="104" width="30" height="12" rx="3" fill="#E5EDA9" /><text x="221" y="113" textAnchor="middle" fill="#082B3D">infra</text>
              <rect x="240" y="104" width="40" height="12" rx="3" fill="#C9DEEF" /><text x="260" y="113" textAnchor="middle" fill="#082B3D">structure</text>
              <rect x="284" y="104" width="22" height="12" rx="3" fill="#FCE3A6" /><text x="295" y="113" textAnchor="middle" fill="#082B3D">for</text>
              <rect x="310" y="104" width="22" height="12" rx="3" fill="#F2C9D2" /><text x="321" y="113" textAnchor="middle" fill="#082B3D">the</text>
              <rect x="180" y="120" width="34" height="12" rx="3" fill="#DDD3EE" /><text x="197" y="129" textAnchor="middle" fill="#082B3D">system</text>
              <rect x="218" y="120" width="34" height="12" rx="3" fill="#FCE3A6" /><text x="235" y="129" textAnchor="middle" fill="#082B3D">prompt</text>
              <rect x="256" y="120" width="22" height="12" rx="3" fill="#F6D4C9" /><text x="267" y="129" textAnchor="middle" fill="#082B3D">you</text>
              <rect x="282" y="120" width="22" height="12" rx="3" fill="#C9DEEF" /><text x="293" y="129" textAnchor="middle" fill="#082B3D">are</text>
              <rect x="308" y="120" width="30" height="12" rx="3" fill="#C8E5DA" /><text x="323" y="129" textAnchor="middle" fill="#082B3D">helpful</text>
              <rect x="160" y="136" width="26" height="12" rx="3" fill="#E5EDA9" /><text x="173" y="145" textAnchor="middle" fill="#082B3D">RAG</text>
              <rect x="190" y="136" width="22" height="12" rx="3" fill="#C8E5DA" /><text x="201" y="145" textAnchor="middle" fill="#082B3D">doc</text>
              <rect x="216" y="136" width="22" height="12" rx="3" fill="#F2C9D2" /><text x="227" y="145" textAnchor="middle" fill="#082B3D">k=3</text>
              <rect x="242" y="136" width="24" height="12" rx="3" fill="#DDD3EE" /><text x="254" y="145" textAnchor="middle" fill="#082B3D">tool</text>
              <rect x="270" y="136" width="30" height="12" rx="3" fill="#FCE3A6" /><text x="285" y="145" textAnchor="middle" fill="#082B3D">resp</text>
              <rect x="304" y="136" width="34" height="12" rx="3" fill="#F6D4C9" /><text x="321" y="145" textAnchor="middle" fill="#082B3D">schema</text>
              <rect x="342" y="136" width="22" height="12" rx="3" fill="#C9DEEF" /><text x="353" y="145" textAnchor="middle" fill="#082B3D">id</text>
              <rect x="148" y="152" width="24" height="12" rx="3" fill="#F6D4C9" /><text x="160" y="161" textAnchor="middle" fill="#082B3D">user</text>
              <rect x="176" y="152" width="26" height="12" rx="3" fill="#FCE3A6" /><text x="189" y="161" textAnchor="middle" fill="#082B3D">msg</text>
              <rect x="206" y="152" width="22" height="12" rx="3" fill="#C9DEEF" /><text x="217" y="161" textAnchor="middle" fill="#082B3D">hist</text>
              <rect x="232" y="152" width="24" height="12" rx="3" fill="#E5EDA9" /><text x="244" y="161" textAnchor="middle" fill="#082B3D">turn</text>
              <rect x="260" y="152" width="22" height="12" rx="3" fill="#C8E5DA" /><text x="271" y="161" textAnchor="middle" fill="#082B3D">+1</text>
              <rect x="286" y="152" width="30" height="12" rx="3" fill="#DDD3EE" /><text x="301" y="161" textAnchor="middle" fill="#082B3D">memory</text>
              <rect x="320" y="152" width="30" height="12" rx="3" fill="#F2C9D2" /><text x="335" y="161" textAnchor="middle" fill="#082B3D">recall</text>
              <rect x="138" y="168" width="22" height="12" rx="3" fill="#E5EDA9" /><text x="149" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="164" y="168" width="22" height="12" rx="3" fill="#C8E5DA" /><text x="175" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="190" y="168" width="22" height="12" rx="3" fill="#F2C9D2" /><text x="201" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="216" y="168" width="24" height="12" rx="3" fill="#DDD3EE" /><text x="228" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="244" y="168" width="22" height="12" rx="3" fill="#FCE3A6" /><text x="255" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="270" y="168" width="22" height="12" rx="3" fill="#F6D4C9" /><text x="281" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="296" y="168" width="22" height="12" rx="3" fill="#C9DEEF" /><text x="307" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="322" y="168" width="22" height="12" rx="3" fill="#E5EDA9" /><text x="333" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="348" y="168" width="22" height="12" rx="3" fill="#C8E5DA" /><text x="359" y="177" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="132" y="184" width="22" height="12" rx="3" fill="#FCE3A6" /><text x="143" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="158" y="184" width="22" height="12" rx="3" fill="#F6D4C9" /><text x="169" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="184" y="184" width="22" height="12" rx="3" fill="#DDD3EE" /><text x="195" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="210" y="184" width="22" height="12" rx="3" fill="#C9DEEF" /><text x="221" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="236" y="184" width="22" height="12" rx="3" fill="#E5EDA9" /><text x="247" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="262" y="184" width="22" height="12" rx="3" fill="#C8E5DA" /><text x="273" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="288" y="184" width="22" height="12" rx="3" fill="#F2C9D2" /><text x="299" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="314" y="184" width="22" height="12" rx="3" fill="#FCE3A6" /><text x="325" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="340" y="184" width="22" height="12" rx="3" fill="#DDD3EE" /><text x="351" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="366" y="184" width="22" height="12" rx="3" fill="#F6D4C9" /><text x="377" y="193" textAnchor="middle" fill="#082B3D">tok</text>
              <rect x="126" y="200" width="18" height="12" rx="3" fill="#C8E5DA" fillOpacity="0.55" />
              <rect x="148" y="200" width="18" height="12" rx="3" fill="#DDD3EE" fillOpacity="0.55" />
              <rect x="170" y="200" width="18" height="12" rx="3" fill="#FCE3A6" fillOpacity="0.55" />
              <rect x="192" y="200" width="18" height="12" rx="3" fill="#F2C9D2" fillOpacity="0.55" />
              <rect x="214" y="200" width="18" height="12" rx="3" fill="#C9DEEF" fillOpacity="0.55" />
              <rect x="236" y="200" width="18" height="12" rx="3" fill="#E5EDA9" fillOpacity="0.55" />
              <rect x="258" y="200" width="18" height="12" rx="3" fill="#F6D4C9" fillOpacity="0.55" />
              <rect x="280" y="200" width="18" height="12" rx="3" fill="#FCE3A6" fillOpacity="0.55" />
              <rect x="302" y="200" width="18" height="12" rx="3" fill="#C8E5DA" fillOpacity="0.55" />
              <rect x="324" y="200" width="18" height="12" rx="3" fill="#DDD3EE" fillOpacity="0.55" />
              <rect x="346" y="200" width="18" height="12" rx="3" fill="#F2C9D2" fillOpacity="0.55" />
              <rect x="368" y="200" width="18" height="12" rx="3" fill="#C9DEEF" fillOpacity="0.55" />
            </g>
            <line x1="105" y1="222" x2="435" y2="222" stroke="#62C6E8" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="4 6" />
            <text x="438" y="220" fontFamily="DM Mono, monospace" fontSize="11" fill="#62C6E8" fillOpacity="0.8">FILL</text>
          </g>

          {/* visor frame on top */}
          <rect x="160" y="240" width="220" height="100" rx="14" fill="none" stroke="#62C6E8" strokeWidth="1.5" />
          <rect x="160" y="240" width="220" height="100" rx="14" fill="none" stroke="#62C6E8" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="3 5" />

          {/* side ear panels */}
          <rect x="100" y="270" width="22" height="70" rx="5" fill="#1A4156" stroke="#62C6E8" strokeWidth="1.2" />
          <rect x="418" y="270" width="22" height="70" rx="5" fill="#1A4156" stroke="#62C6E8" strokeWidth="1.2" />
          <circle cx="111" cy="290" r="3" fill="#62C6E8" />
          <circle cx="111" cy="305" r="3" fill="#62C6E8" fillOpacity="0.7" />
          <circle cx="429" cy="290" r="3" fill="#62C6E8" />
          <circle cx="429" cy="305" r="3" fill="#62C6E8" fillOpacity="0.7" />

          {/* jaw / mouth grille */}
          <rect x="210" y="410" width="120" height="22" rx="4" fill="#0E455E" stroke="#62C6E8" strokeWidth="1.2" />

          {/* crown bolts */}
          <circle cx="170" cy="120" r="5" fill="#62C6E8" fillOpacity="0.55" />
          <circle cx="370" cy="120" r="5" fill="#62C6E8" fillOpacity="0.55" />

          {/* subtle shine */}
          <path d="M180 80 Q 220 60 270 60" stroke="#62C6E8" strokeOpacity="0.35" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>

        <div className="legend">
          <span>Filling the brain · top-down</span>
          <div className="dots">
            <span style={{ background: '#C8E5DA' }} />
            <span style={{ background: '#F6D4C9' }} />
            <span style={{ background: '#DDD3EE' }} />
            <span style={{ background: '#E5EDA9' }} />
            <span style={{ background: '#FCE3A6' }} />
            <span style={{ background: '#C9DEEF' }} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  6 — Image Placeholder
   ══════════════════════════════════════════════════════════════════════ */

const Slide06 = () => (
  <div className="slide-body design-slide">
    <div className="frame s4">
      <div>
        <h1 className="title">The anatomy of <em>context</em></h1>
        <p className="s3-intro" style={{ maxWidth: 1100, marginTop: '-14px' }}>
          Some content gets <strong>placed once and stays put</strong>. The rest is a moving stream — fresh data flowing through the window every turn.
        </p>
      </div>

      <div className="anatomy">
        {/* STATIC LANE */}
        <div className="lane">
          <div className="lane-head">
            <h3>Static · pinned</h3>
            <span className="tag tag-static">Set once · stays</span>
          </div>
          <p className="lane-desc">
            Loaded at the start of the run. The model sees the same bytes on every turn — no replay, no refresh.
          </p>

          <div className="pinned">
            <div className="pin-card">System prompt <span className="meta">~1.2k tok</span></div>
            <div className="pin-card">Tool definitions <span className="meta">~3.4k tok</span></div>
            <div className="pin-card">Structured-output schema <span className="meta">~0.8k tok</span></div>
            <div className="pin-card">Guardrails &amp; policy <span className="meta">~0.6k tok</span></div>
          </div>

          <div className="static-foot">
            <span>Anchored · cache-friendly</span>
            <span>~6k tokens</span>
          </div>
        </div>

        {/* DYNAMIC LANE */}
        <div className="lane">
          <div className="lane-head">
            <h3>Dynamic · streaming</h3>
            <span className="tag tag-dynamic">Re-assembled every turn</span>
          </div>
          <p className="lane-desc">
            Fresh content flows in turn-by-turn — and gets evicted when the window fills. This is the part you have to engineer.
          </p>

          <div className="stream">
            <div className="stream-row r1">
              <span className="chip user"><span className="dot" />user msg</span>
              <span className="chip rag"><span className="dot" />doc · k=3</span>
              <span className="chip mem"><span className="dot" />history</span>
              <span className="chip tool"><span className="dot" />tool call</span>
              <span className="chip"><span className="dot" />thought</span>
              <span className="chip rag"><span className="dot" />passage</span>
              <span className="chip user"><span className="dot" />user msg</span>
              <span className="chip rag"><span className="dot" />doc · k=3</span>
              <span className="chip mem"><span className="dot" />history</span>
              <span className="chip tool"><span className="dot" />tool call</span>
              <span className="chip"><span className="dot" />thought</span>
              <span className="chip rag"><span className="dot" />passage</span>
            </div>
            <div className="stream-row r2">
              <span className="chip mem"><span className="dot" />long-term mem</span>
              <span className="chip tool"><span className="dot" />tool result</span>
              <span className="chip user"><span className="dot" />follow-up</span>
              <span className="chip rag"><span className="dot" />k=5 hits</span>
              <span className="chip"><span className="dot" />plan step</span>
              <span className="chip mem"><span className="dot" />long-term mem</span>
              <span className="chip tool"><span className="dot" />tool result</span>
              <span className="chip user"><span className="dot" />follow-up</span>
              <span className="chip rag"><span className="dot" />k=5 hits</span>
              <span className="chip"><span className="dot" />plan step</span>
            </div>
            <div className="stream-row r3">
              <span className="chip rag"><span className="dot" />doc fragment</span>
              <span className="chip user"><span className="dot" />clarify</span>
              <span className="chip tool"><span className="dot" />API resp</span>
              <span className="chip mem"><span className="dot" />summary</span>
              <span className="chip"><span className="dot" />scratchpad</span>
              <span className="chip rag"><span className="dot" />doc fragment</span>
              <span className="chip user"><span className="dot" />clarify</span>
              <span className="chip tool"><span className="dot" />API resp</span>
              <span className="chip mem"><span className="dot" />summary</span>
              <span className="chip"><span className="dot" />scratchpad</span>
            </div>
            <div className="stream-row r4">
              <span className="chip tool"><span className="dot" />weather()</span>
              <span className="chip rag"><span className="dot" />chunk</span>
              <span className="chip user"><span className="dot" />turn 7</span>
              <span className="chip"><span className="dot" />thought</span>
              <span className="chip mem"><span className="dot" />recall</span>
              <span className="chip tool"><span className="dot" />weather()</span>
              <span className="chip rag"><span className="dot" />chunk</span>
              <span className="chip user"><span className="dot" />turn 7</span>
              <span className="chip"><span className="dot" />thought</span>
              <span className="chip mem"><span className="dot" />recall</span>
            </div>
            <div className="stream-row r5">
              <span className="chip rag"><span className="dot" />retrieval</span>
              <span className="chip mem"><span className="dot" />turn-history</span>
              <span className="chip tool"><span className="dot" />db query</span>
              <span className="chip user"><span className="dot" />new ask</span>
              <span className="chip"><span className="dot" />response</span>
              <span className="chip rag"><span className="dot" />retrieval</span>
              <span className="chip mem"><span className="dot" />turn-history</span>
              <span className="chip tool"><span className="dot" />db query</span>
              <span className="chip user"><span className="dot" />new ask</span>
              <span className="chip"><span className="dot" />response</span>
            </div>
            <div className="stream-axis">flow direction · turn → turn</div>
          </div>

          <div className="lane-foot">
            <span>Engineered · assembled per call</span>
            <span>budget: remaining tokens</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  7 — Context Window
   ══════════════════════════════════════════════════════════════════════ */

const Slide07 = () => (
  <div className="slide-body design-slide">
    <div className="frame s3">
      <div className="s3-head">
        <div>
          <h1 className="title">What is a <em>context window?</em></h1>
        </div>
        <p className="s3-intro">
          A <strong>fixed budget</strong> the model thinks inside of. Your inputs and the model{'\''}s output share the same space — when the budget runs out, something has to give.
        </p>
      </div>

      <div className="window-diagram">
        <div className="win-label">
          <span className="lhs">Context window — model: claude-3.5-sonnet</span>
          <span className="rhs">capacity: 200,000 tokens</span>
        </div>

        <div className="win-card input">
          <span className="sub">Your input</span>
          <h3>What goes in</h3>
          <ul>
            <li>System &amp; user prompts</li>
            <li>Conversation history</li>
            <li>Retrieved documents</li>
            <li>Tool definitions &amp; results</li>
          </ul>
        </div>

        <div className="win-card output">
          <span className="sub">Model output</span>
          <h3>What comes back</h3>
          <ul>
            <li>The agent{'\''}s response</li>
            <li>Tool calls it decides to make</li>
            <li>Reasoning &amp; thoughts</li>
            <li>Continuations on next turn</li>
          </ul>
        </div>

        <div className="win-card cap">
          <span className="sub">Same space, shared</span>
          <h3>Fixed capacity</h3>
          <div className="num">200<span>K</span></div>
          <ul style={{ fontSize: 18 }}>
            <li>Counted in tokens, not words</li>
            <li>Earlier turns get pushed out</li>
            <li>Bigger ≠ free — costs &amp; latency rise</li>
          </ul>
        </div>

        <div className="win-meter">
          <span className="lab">Window fill</span>
          <div className="bar">
            <div className="bar-input" />
            <div className="bar-output" />
            <div className="bar-free" />
          </div>
          <span className="num">120K used · 80K free</span>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  8 — Image Placeholder
   ══════════════════════════════════════════════════════════════════════ */

const Slide08 = () => (
  <div className="slide-body design-slide">
    <div className="frame s5">
      <div>
        <h1 className="title">Context engineering is <em>curation</em></h1>
        <p className="s3-intro" style={{ maxWidth: 1100 }}>
          The window is fixed. The conversation isn{'\''}t. Every turn, you decide what new tokens earn a seat — and what older tokens get pushed out.
        </p>
      </div>

      <div className="ribbon-wrap">
        <div className="ribbon-head">
          <span className="lhs">Conversation timeline · turns 1 → N</span>
          <span className="rhs">window slides forward each turn</span>
        </div>

        <div className="ribbon-axis">
          <div className="axis-cell drop"><span className="pill">↤ Drop · evict</span><span>oldest, lowest value</span></div>
          <div className="axis-cell keep"><span className="pill">◇ Keep · in window</span></div>
          <div className="axis-cell add"><span>fresh, on-topic</span><span className="pill">Add · admit ↦</span></div>
        </div>

        <div className="ribbon">
          <div className="window-frame" />

          <div className="rblock rb-old">turn 1<br />greeting</div>
          <div className="rblock rb-old">turn 2<br />old doc</div>
          <div className="rblock rb-old">turn 3<br />tool resp</div>
          <div className="rblock rb-mem">turn 4<br />summary</div>
          <div className="rblock rb-rag">turn 5<br />RAG k=3</div>
          <div className="rblock rb-user">turn 6<br />user msg</div>
          <div className="rblock rb-tool">turn 7<br />weather()</div>
          <div className="rblock rb-out">turn 7<br />response</div>
          <div className="rblock rb-user">turn 8<br />follow-up</div>
          <div className="rblock rb-rag">turn 8<br />passages</div>
          <div className="rblock rb-tool">turn 9<br />db query</div>
          <div className="rblock rb-out">turn 9<br />response</div>
          <div className="rblock rb-user">turn 10<br />new ask</div>
          <div className="rblock rb-mem">turn 10<br />recall</div>
          <div className="rblock rb-rag">turn 11<br />chunks</div>
          <div className="rblock rb-tool">turn 11<br />API call</div>
          <div className="rblock rb-out">turn 11<br />response</div>
        </div>

        <div className="timeline-axis">
          <div className="track" />
          <span className="tick">turn 1 · start</span>
          <span className="tick center">conversation grows → window slides forward over time</span>
          <span className="tick">turn N · now</span>
        </div>

        <div className="decisions">
          <div className="decision d-drop">
            <span className="lab">Decide what to drop</span>
            <h4>Stale, redundant, low-signal</h4>
            <p>Old tool dumps, duplicate retrievals, off-topic turns. Summarize or evict before they crowd out fresh data.</p>
          </div>
          <div className="decision d-keep">
            <span className="lab">Decide what to keep</span>
            <h4>Anchors &amp; recent state</h4>
            <p>System prompt, the live task, recent reasoning. The pinned spine plus enough recency to stay coherent.</p>
          </div>
          <div className="decision d-add">
            <span className="lab">Decide what to add</span>
            <h4>Right tokens, just in time</h4>
            <p>Only what this turn needs — top-k retrievals, the tool result you{'\''}ll cite, the user{'\''}s new instruction.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide  9 — Four Pillars
   ══════════════════════════════════════════════════════════════════════ */

const pillars = [
  {
    num: '01',
    name: 'Memory Management',
    body: 'Separate short-term state from long-term memory. Persistent facts across sessions.',
    where: 'Lives in the agent framework (LangGraph, ADK)',
  },
  {
    num: '02',
    name: 'Tool Orchestration',
    body: 'Curate a minimal, well-described set of tools the agent can call. Fewer, sharper tools beat dozens.',
    where: 'Lives in the agent framework',
  },
  {
    num: '03',
    name: 'Compaction & Pruning',
    body: 'Summarize histories so they don\'t fill the window with noise. Defend against context rot.',
    where: 'YOUR STACK: State management & observability',
  },
  {
    num: '04',
    name: 'Dynamic Information Flow',
    body: 'Inject high-signal data just-in-time from streams, vector stores, knowledge graphs.',
    where: 'YOUR STACK: Kafka provides this. Context assembly lives here.',
  },
];

const Slide09 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>The Four Pillars of <em style={S.accent}>Context Engineering</em></h2>
    <div style={S.fourGrid}>
      {pillars.map(p => (
        <div key={p.num} style={{ ...S.card, padding: '2rem' }}>
          <div style={{ ...S.muted, fontSize: '1.26rem', marginBottom: '0.5rem' }}>{p.num}</div>
          <h3 style={{ ...S.h3, fontSize: '1.59rem' }}>{p.name}</h3>
          <p style={{ fontSize: '1.34rem', lineHeight: 1.55, opacity: 0.85, margin: '0 0 0.75rem' }}>
            {p.body}
          </p>
          <div style={{
            ...S.tag,
            fontSize: '1.14rem',
            ...(p.num === '03' || p.num === '04' ? { background: 'rgba(52,211,153,0.15)', color: '#34d399' } : {}),
          }}>
            {p.where}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 10 — Eager vs. Lazy
   ══════════════════════════════════════════════════════════════════════ */

const Slide10 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>Two Philosophies: <em style={S.accent}>Eager</em> vs. <em style={S.accent}>Lazy</em></h2>
    <div style={{ ...S.twoCol, flex: 1, gap: '2.5rem' }}>
      {/* Eager */}
      <div style={{ ...S.card, flex: 1, padding: '2.5rem', display: 'flex', flexDirection: 'column' as const, gap: '1.25rem' }}>
        <div style={{ ...S.tagGreen, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>EAGER</div>
        <p style={{ fontSize: '1.6rem', lineHeight: 1.5, margin: 0, opacity: 0.92 }}>
          The data plane assembles context <em>BEFORE</em> the agent reasons. Joins are
          pre-computed; windows are pre-aggregated; noise is pre-filtered.
        </p>
        <div style={{ ...S.card, padding: '1.75rem 2rem', marginTop: 'auto', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)' }}>
          <div style={{ ...S.label, fontSize: '1.05rem' }}><span style={{ fontSize: '1.725rem', marginRight: '0.4rem', verticalAlign: 'middle' }}>🎒</span> BACKPACK</div>
          <p style={{ fontSize: '1.3rem', lineHeight: 1.5, margin: '0.4rem 0 0', opacity: 0.8 }}>
            The event arrives at the agent with everything already packed.
          </p>
        </div>
      </div>
      {/* Lazy */}
      <div style={{ ...S.card, flex: 1, padding: '2.5rem', display: 'flex', flexDirection: 'column' as const, gap: '1.25rem' }}>
        <div style={{ ...S.tagOrange, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>LAZY</div>
        <p style={{ fontSize: '1.6rem', lineHeight: 1.5, margin: 0, opacity: 0.92 }}>
          The agent assembles context <em>AT REASONING TIME</em>. State stores hold raw data;
          tools fetch on demand.
        </p>
        <div style={{ ...S.card, padding: '1.75rem 2rem', marginTop: 'auto', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
          <div style={{ ...S.label, fontSize: '1.05rem' }}><span style={{ fontSize: '1.725rem', marginRight: '0.4rem', verticalAlign: 'middle' }}>🛒</span> GROCERY LIST</div>
          <p style={{ fontSize: '1.3rem', lineHeight: 1.5, margin: '0.4rem 0 0', opacity: 0.8 }}>
            The event arrives at the agent with a list of what to fetch. The agent goes shopping.
          </p>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 11 — Section Divider: Why a New Pattern?
   ══════════════════════════════════════════════════════════════════════ */

const infrastructureRows = [
  { demand: 'Latency', means: 'Sub-500ms tool calls', pattern: 'Streaming substrate (Kafka, not batch)' },
  { demand: 'Staleness', means: 'Stale data → wrong action', pattern: 'Watermarked stream-table joins' },
  { demand: 'Consumption', means: 'Context on event arrival', pattern: 'Real-time context engine (push or pull)' },
  { demand: 'Reasoning', means: 'Bounded context window', pattern: 'Backpressure & pruning at the data plane', highlight: true },
  { demand: 'Error handling', means: 'Silent propagation', pattern: 'Context-level observability (Reasoning SLOs)' },
  { demand: 'Failure cost', means: 'Cascading mesh failures', pattern: 'Event sourcing — replay, audit, source filtering', highlight: true },
] as const;

const infraCell = {
  padding: '18px 24px',
  fontSize: '22px',
  lineHeight: 1.5,
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  verticalAlign: 'middle' as const,
} as React.CSSProperties;

const Slide11 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h1 style={S.h1}>
      Why a New <em style={S.accent}>Infrastructure Pattern</em>?
    </h1>
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
      <thead>
        <tr>
          <th style={{ ...infraCell, background: '#082B3D', color: '#fff', fontWeight: 700, fontSize: '24px', width: '20%', borderBottom: 'none' }}>Agent Demand</th>
          <th style={{ ...infraCell, background: '#082B3D', color: '#fff', fontWeight: 700, fontSize: '24px', width: '35%', borderBottom: 'none' }}>What It Means</th>
          <th style={{ ...infraCell, background: '#082B3D', color: '#fff', fontWeight: 700, fontSize: '24px', width: '45%', borderBottom: 'none' }}>Infrastructure Pattern</th>
        </tr>
      </thead>
      <tbody>
        {infrastructureRows.map((row, i) => (
          <tr key={i} style={{ background: ('highlight' in row && row.highlight) ? '#FFF8E1' : (i % 2 === 0 ? '#fff' : '#f8f9fa') }}>
            <td style={{ ...infraCell, background: '#E5F1F7', color: '#0F86C7', fontWeight: 700, fontSize: '22px' }}>{row.demand}</td>
            <td style={infraCell}>{row.means}</td>
            <td style={{ ...infraCell, ...(('highlight' in row && row.highlight) ? { color: '#C8860A', fontWeight: 700 } : {}) }}>{row.pattern}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 12 — Challenge #1: Context Assembly
   ══════════════════════════════════════════════════════════════════════ */

const Slide12 = () => (
  <div className="slide-body design-slide">
    <div className="frame s6">
      <div>
        <h1 className="title">Challenge #1: <em>Context Assembly</em></h1>
        <p className="s3-intro" style={{ fontSize: '27px', maxWidth: 1400 }}>
          The new tokens flowing into the window come from <strong>many Kafka topics</strong> — each on its own clock, schema, and rate. Assembly is picking the right slice of each one, every turn.
        </p>
      </div>

      <div className="assembly-stage" style={{ gridTemplateColumns: '0.85fr auto 1.15fr', gridTemplateRows: '1fr' }}>
        {/* Left: raw click event */}
        <div>
          <div className="col-head"><span>Raw click event</span><span className="badge">4 fields</span></div>
          <pre style={{
            fontFamily: "'DM Mono', monospace", fontSize: '15px', lineHeight: 1.65,
            background: '#FAF6EE', border: '1px solid rgba(8,43,61,0.12)', borderRadius: '10px',
            padding: '18px 20px', color: 'var(--navy)', margin: 0, whiteSpace: 'pre' as const,
          }}><code>{`{
  "user_id":    "u_8847291",
  "action":     "click",
  "product_id": "CAM-X200",
  "ts":         "2026-05-20T14:32:07Z"
}`}</code></pre>
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column' as const, gap: '8px' }}>
            <div className="col-head" style={{ marginBottom: 0 }}><span>Sources needed</span><span className="badge">5 topics</span></div>
            {[
              { name: 'clicks_stream', color: 'var(--rose)', rate: '~5k/s' },
              { name: 'purchases_stream', color: 'var(--cyan)', rate: '~50/s' },
              { name: 'product_catalog', color: 'var(--warm)', rate: 'versioned' },
              { name: 'inventory_levels', color: 'var(--sage)', rate: '~100/s' },
              { name: 'user_profile', color: 'var(--lilac)', rate: 'CDC' },
            ].map((t, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                borderLeft: `4px solid ${t.color}`, padding: '4px 10px',
                fontFamily: "'DM Mono', monospace", fontSize: '13px', color: 'var(--navy)',
              }}>
                <span style={{ flex: 1, fontWeight: 600 }}>{t.name}</span>
                <span style={{ opacity: 0.5, fontSize: '11px' }}>{t.rate}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center: arrow */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' }}>
          <svg viewBox="0 0 60 200" style={{ width: '40px', height: '160px' }} xmlns="http://www.w3.org/2000/svg">
            <defs>
              <marker id="assemArrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                <path d="M0,0 L8,3 L0,6" fill="var(--cyan)" />
              </marker>
            </defs>
            <line x1="10" y1="100" x2="48" y2="100" stroke="var(--cyan)" strokeWidth="2.5" markerEnd="url(#assemArrow)" />
            <text x="30" y="85" textAnchor="middle" fill="var(--navy)" fontSize="9" fontWeight="700" opacity="0.5">ASSEMBLE</text>
          </svg>
        </div>

        {/* Right: enriched context record */}
        <div>
          <div className="col-head"><span>Enriched context record</span><span className="badge">11 fields</span></div>
          <pre style={{
            fontFamily: "'DM Mono', monospace", fontSize: '13.5px', lineHeight: 1.55,
            background: '#FAF6EE', border: '1px solid rgba(8,43,61,0.12)', borderRadius: '10px',
            padding: '16px 18px', color: 'var(--navy)', margin: 0, whiteSpace: 'pre' as const,
            flex: 1,
          }}><code>{`{
  `}<span style={{ color: '#E07B7B' }}>{`"user_id":        "u_8847291",
  "action":         "click",
  "product_id":     "CAM-X200",
  "ts":             "2026-05-20T14:32:07Z",`}</span>{`
  `}<span style={{ color: '#E8B26A' }}>{`"product_name":   "ProShot X200",
  "price":           1299.00,
  "category":       "mirrorless",`}</span>{`
  `}<span style={{ color: '#7AAE85' }}>{`"stock_level":     23,`}</span>{`
  `}<span style={{ color: '#9C8FC9' }}>{`"shipping_zip":   "EC2A 4NE",
  "predicted_size": "compact",`}</span>{`
  `}<span style={{ color: '#0F86C7' }}>{`"return_count_30d": 1`}</span>{`
}`}</code></pre>
          <div style={{
            marginTop: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap' as const,
            fontFamily: "'DM Mono', monospace", fontSize: '11px', opacity: 0.6,
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#E07B7B' }} />clicks</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#0F86C7' }} />purchases</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#E8B26A' }} />catalog</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#7AAE85' }} />inventory</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#9C8FC9' }} />profile</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 13 — Eager vs Lazy: The Join
   ══════════════════════════════════════════════════════════════════════ */

const Slide13 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>Eager vs. Lazy: <em style={S.accent}>The Join</em></h2>
    <div style={S.twoCol}>
      {/* Lazy */}
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tagOrange, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>LAZY (ORCA PYTHON)</div>
        <p style={{ fontSize: '1.2rem', opacity: 0.7, margin: 0 }}>
          The agent receives the raw click and makes five state-store lookups before it can reason.
        </p>
        <pre style={{ ...S.code, fontSize: '14px', padding: '1.25rem 1.5rem' }}><code>{`def on_message(self, event):
    product = self.state.get(
        'products', event['product_id'])
    user = self.state.get(
        'users', event['user_id'])
    inventory = self.state.get(
        'inventory', event['product_id'])
    # ... more lookups
    context = build(
        event, product, user, inventory)
    return self.agent.run(context)`}</code></pre>
      </div>
      {/* Eager */}
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tagGreen, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>EAGER (FLINK SQL)</div>
        <p style={{ fontSize: '1.2rem', opacity: 0.7, margin: 0 }}>
          A 5-table temporal join &mdash; clicks, catalog, inventory, profile, and a 30-day returns view.
        </p>
        <pre style={{ ...S.code, fontSize: '14px', padding: '1.25rem 1.5rem' }}><code>{`SELECT c.user_id, c.action,
       c.product_id, c.ts,
       p.product_name, p.price,
       p.category,
       i.stock_level,
       u.shipping_zip, u.predicted_size,
       r.return_count_30d
FROM   clicks_stream c
JOIN   product_catalog
  FOR SYSTEM_TIME AS OF c.ts p
  ON c.product_id = p.product_id
JOIN   inventory_levels
  FOR SYSTEM_TIME AS OF c.ts i
  ON c.product_id = i.product_id
JOIN   user_profile
  FOR SYSTEM_TIME AS OF c.ts u
  ON c.user_id = u.user_id
LEFT JOIN returns_30d r
  ON c.user_id = r.user_id`}</code></pre>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 14 — Eager Context Assembly Diagram
   ══════════════════════════════════════════════════════════════════════ */

/* ── Animated records feed for the assembled column ── */
const allRecords = [
  { key: 'u_312', ts: '14:32:04', colors: ['rgba(224,123,123,0.7)','rgba(15,134,199,0.7)','rgba(15,134,199,0.7)','rgba(232,178,106,0.85)','rgba(122,174,133,0.75)','rgba(156,143,201,0.75)'] },
  { key: 'u_523', ts: '14:32:05', colors: ['rgba(224,123,123,0.7)','rgba(224,123,123,0.7)','rgba(232,178,106,0.85)','rgba(232,178,106,0.85)','rgba(122,174,133,0.75)','rgba(156,143,201,0.75)'] },
  { key: 'u_847', ts: '14:32:06', colors: ['rgba(224,123,123,0.7)','rgba(15,134,199,0.7)','rgba(232,178,106,0.85)','rgba(122,174,133,0.75)','rgba(122,174,133,0.75)','rgba(156,143,201,0.75)'] },
  { key: 'u_847', ts: '14:32:07', colors: ['rgba(224,123,123,0.85)','rgba(15,134,199,0.85)','rgba(232,178,106,0.95)','rgba(122,174,133,0.85)','rgba(156,143,201,0.85)'] },
  { key: 'u_109', ts: '14:32:08', colors: ['rgba(15,134,199,0.7)','rgba(232,178,106,0.85)','rgba(224,123,123,0.7)','rgba(156,143,201,0.75)','rgba(122,174,133,0.75)','rgba(15,134,199,0.7)'] },
  { key: 'u_523', ts: '14:32:09', colors: ['rgba(232,178,106,0.85)','rgba(224,123,123,0.7)','rgba(122,174,133,0.75)','rgba(15,134,199,0.7)','rgba(156,143,201,0.75)'] },
  { key: 'u_312', ts: '14:32:10', colors: ['rgba(224,123,123,0.85)','rgba(232,178,106,0.95)','rgba(15,134,199,0.85)','rgba(122,174,133,0.85)','rgba(156,143,201,0.85)','rgba(224,123,123,0.7)'] },
  { key: 'u_641', ts: '14:32:11', colors: ['rgba(156,143,201,0.75)','rgba(15,134,199,0.7)','rgba(224,123,123,0.7)','rgba(232,178,106,0.85)','rgba(122,174,133,0.75)'] },
];
const MAX_VISIBLE = 9;

const AnimatedRecords = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIdx(i => i + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  // Build visible list: most recent at top, max MAX_VISIBLE shown
  const endIdx = idx + 1;
  const startIdx = Math.max(0, endIdx - MAX_VISIBLE);
  const visible = [];
  for (let i = endIdx - 1; i >= startIdx; i--) {
    visible.push(allRecords[i % allRecords.length]);
  }
  return (
    <div className="records">
      {visible.map((r, vi) => (
        <div key={`${r.key}-${r.ts}-${idx}-${vi}`} className={`record${vi === 0 ? ' latest' : ''}`}>
          <span className="rkey">{r.key} · {r.ts}</span>
          <div className="rtokens">
            {r.colors.map((c, ci) => <span key={ci} style={{ background: c }} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

const Slide14 = () => (
  <div className="slide-body design-slide">
    <div className="frame s7">
      <div>
        <h1 className="title">Eager <em>context assembly</em></h1>
        <p className="s3-intro" style={{ fontSize: '25px', maxWidth: 1400 }}>
          A <strong>continuously running Flink job</strong> joins every source into materialized context tables. A <strong>temporal join</strong> aligns events by time. The result is <strong>queryable</strong> — filters, ranges, projections — served to agents via MCP at low latency.
        </p>
      </div>

      <div className="ea-stage">
        <div className="ea-pipeline">
          {/* Topics column */}
          <div className="topics-col">
            <div className="col-head"><span>Source topics</span><span className="badge">streaming</span></div>
            <div className="topic-flat t1"><span className="tname">clicks_stream</span><span className="rate">~5k/s</span><div className="partitions"><span className="seg" /><span className="seg" /><span className="seg" /><span className="seg" /><span className="seg" /><span className="seg" /></div></div>
            <div className="topic-flat t2"><span className="tname">purchases_stream</span><span className="rate">~50/s</span><div className="partitions"><span className="seg" /><span className="seg" /><span className="seg empty" /><span className="seg empty" /><span className="seg empty" /><span className="seg empty" /></div></div>
            <div className="topic-flat t3"><span className="tname">product_catalog</span><span className="rate">versioned</span><div className="partitions"><span className="seg" /><span className="seg" /><span className="seg" /><span className="seg empty" /><span className="seg empty" /><span className="seg empty" /></div></div>
            <div className="topic-flat t4"><span className="tname">inventory_levels</span><span className="rate">~100/s</span><div className="partitions"><span className="seg" /><span className="seg" /><span className="seg" /><span className="seg empty" /><span className="seg empty" /><span className="seg empty" /></div></div>
            <div className="topic-flat t5"><span className="tname">user_profile</span><span className="rate">CDC</span><div className="partitions"><span className="seg" /><span className="seg" /><span className="seg empty" /><span className="seg empty" /><span className="seg empty" /><span className="seg empty" /></div></div>
          </div>

          {/* Arrows in */}
          <div className="arrows-in">
            <div className="ai-row t1"><div className="line" /><div className="head" /></div>
            <div className="ai-row t2"><div className="line" /><div className="head" /></div>
            <div className="ai-row t3"><div className="line" /><div className="head" /></div>
            <div className="ai-row t4"><div className="line" /><div className="head" /></div>
            <div className="ai-row t5"><div className="line" /><div className="head" /></div>
          </div>

          {/* Flink job */}
          <div className="flink-v2">
            <span className="lab-eye">Flink job</span>
            <h3>Always-on temporal join</h3>
            <span className="running"><span className="dot" />RUNNING · backfilling watermark t−Δ</span>
            <div className="join-viz">
              <div className="jv-head">
                <span>Temporal join</span>
                <span>watermark · t = 14:32:07</span>
              </div>
              <svg className="jv-svg" viewBox="0 0 320 150" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="outPipe" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="rgba(98,198,232,0.0)" />
                    <stop offset="20%" stopColor="rgba(98,198,232,0.5)" />
                    <stop offset="100%" stopColor="rgba(98,198,232,0.85)" />
                  </linearGradient>
                </defs>
                {/* 5 input strands converging */}
                <path d="M 0 14  C 110 14,  140 75, 180 75" stroke="rgba(224,123,123,0.95)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <path d="M 0 46  C 110 46,  150 75, 180 75" stroke="rgba(15,134,199,0.95)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <path d="M 0 75  L 180 75" stroke="rgba(232,178,106,1)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <path d="M 0 104 C 110 104, 150 75, 180 75" stroke="rgba(122,174,133,0.95)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <path d="M 0 136 C 110 136, 140 75, 180 75" stroke="rgba(156,143,201,0.95)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                {/* flowing dots */}
                <circle r="3" fill="rgba(224,123,123,1)"><animateMotion dur="3.6s" repeatCount="indefinite" path="M 0 14 C 110 14, 140 75, 180 75" /></circle>
                <circle r="3" fill="rgba(15,134,199,1)"><animateMotion dur="4.4s" repeatCount="indefinite" path="M 0 46 C 110 46, 150 75, 180 75" /></circle>
                <circle r="3" fill="rgba(232,178,106,1)"><animateMotion dur="3.0s" repeatCount="indefinite" path="M 0 75 L 180 75" /></circle>
                <circle r="3" fill="rgba(122,174,133,1)"><animateMotion dur="4.0s" repeatCount="indefinite" path="M 0 104 C 110 104, 150 75, 180 75" /></circle>
                <circle r="3" fill="rgba(156,143,201,1)"><animateMotion dur="5.2s" repeatCount="indefinite" path="M 0 136 C 110 136, 140 75, 180 75" /></circle>
                {/* barrier / junction node */}
                <circle cx="180" cy="75" r="14" fill="none" stroke="rgba(98,198,232,0.45)" strokeWidth="1" strokeDasharray="3 3" />
                <circle cx="180" cy="75" r="7" fill="#62C6E8" />
                <circle cx="180" cy="75" r="11" fill="none" stroke="#62C6E8" strokeWidth="1" opacity="0.7">
                  <animate attributeName="r" values="7;18;7" dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0;0.8" dur="2.4s" repeatCount="indefinite" />
                </circle>
                {/* output pipe */}
                <line x1="180" y1="75" x2="320" y2="75" stroke="url(#outPipe)" strokeWidth="14" strokeLinecap="round" />
                {/* segmented merged record */}
                <g>
                  <rect x="-30" y="71" width="6" height="8" fill="rgba(224,123,123,1)" rx="1" />
                  <rect x="-22" y="71" width="6" height="8" fill="rgba(15,134,199,1)" rx="1" />
                  <rect x="-14" y="71" width="6" height="8" fill="rgba(232,178,106,1)" rx="1" />
                  <rect x="-6" y="71" width="6" height="8" fill="rgba(122,174,133,1)" rx="1" />
                  <rect x="2" y="71" width="6" height="8" fill="rgba(156,143,201,1)" rx="1" />
                  <animateTransform attributeName="transform" type="translate" from="200 0" to="350 0" dur="3.2s" repeatCount="indefinite" />
                </g>
              </svg>
              <div className="jv-foot">
                <span className="jv-pill">5 in</span>
                <span className="jv-arrow">→</span>
                <span className="jv-pill jv-pill-out">1 record per (key, time)</span>
              </div>
            </div>
          </div>

          {/* Arrow out */}
          <div className="arrow-out">
            <div className="ao-line" />
            <div className="ao-strip">
              <span style={{ background: 'rgba(224,123,123,0.85)' }} />
              <span style={{ background: 'rgba(15,134,199,0.85)' }} />
              <span style={{ background: 'rgba(232,178,106,0.95)' }} />
              <span style={{ background: 'rgba(122,174,133,0.85)' }} />
              <span style={{ background: 'rgba(156,143,201,0.85)' }} />
            </div>
          </div>

          {/* context.assembled topic — animated feed */}
          <div className="assembled-col">
            <div className="col-head"><span>→ context.assembled</span><span className="badge">kafka topic</span></div>
            <AnimatedRecords />
          </div>
        </div>

        {/* Row 2: consume link + agent + MCP tool */}
        <div className="ea-row2">
          <div className="r2-consume">
            <div className="consume-link">
              <span className="cl-arrow">↓</span>
              <span>consume latest by user_id</span>
            </div>
          </div>
          <div className="agent-mcp">
            <div className="agent-card">
              <svg className="robot-mini" viewBox="0 0 80 96" xmlns="http://www.w3.org/2000/svg">
                <line x1="40" y1="6" x2="40" y2="16" stroke="#0B3A50" strokeWidth="2.5" />
                <circle cx="40" cy="6" r="3.5" fill="#0F86C7" />
                <rect x="14" y="16" width="52" height="42" rx="14" fill="#fff" stroke="#0B3A50" strokeWidth="2.5" />
                <rect x="20" y="26" width="40" height="18" rx="6" fill="#0B3A50" />
                <circle cx="30" cy="35" r="3" fill="#5DD4FF" />
                <circle cx="50" cy="35" r="3" fill="#5DD4FF" />
                <circle cx="14" cy="38" r="3" fill="#0B3A50" />
                <circle cx="66" cy="38" r="3" fill="#0B3A50" />
                <line x1="32" y1="51" x2="48" y2="51" stroke="#0B3A50" strokeWidth="2" strokeLinecap="round" />
                <rect x="22" y="60" width="36" height="22" rx="6" fill="#fff" stroke="#0B3A50" strokeWidth="2.5" />
                <line x1="40" y1="66" x2="40" y2="78" stroke="#0B3A50" strokeWidth="1.5" />
              </svg>
              <span className="ag-name">AI agent</span>
              <span className="ag-eye">consumer · turn 47</span>
            </div>
            <div className="agent-link">
              <div className="al-arrow al-call"><div className="line" /><span className="label">tool call</span></div>
              <div className="al-arrow al-resp"><div className="line" /><span className="label">tokens returned</span></div>
            </div>
            <div className="mcp-card">
              <div className="mcp-icon" />
              <div className="mcp-meta">
                <span className="tc-eye">MCP server tool</span>
                <span className="tc-name">get_context(user_id)</span>
                <span className="tc-ret">↵ returns latest record · all tokens for the turn</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 15 — Lazy Context Assembly Diagram
   ══════════════════════════════════════════════════════════════════════ */

const Slide15 = () => (
  <div className="slide-body design-slide">
    <div className="frame s8">
      <div>
        <h1 className="title">Lazy <em>context assembly</em></h1>
        <p className="s3-intro" style={{ maxWidth: 1400, fontSize: '26px' }}>
          No pre-joined record. Each token is pulled <strong>on demand</strong> — a state-store lookup, a <strong>dynamically discovered MCP tool</strong> call, even a message to <strong>another agent</strong> — and dropped into the context as it returns. Calls fan out <strong>in parallel</strong>; the window assembles itself as results arrive.
        </p>
      </div>

      <div className="la-stage la-stage-animated">
        {/* Agent column */}
        <div className="la-agent-col">
          <svg className="robot-mini-lg" viewBox="0 0 80 96" xmlns="http://www.w3.org/2000/svg">
            <line x1="40" y1="6" x2="40" y2="16" stroke="#0B3A50" strokeWidth="2.5" />
            <circle cx="40" cy="6" r="3.5" fill="#0F86C7" />
            <rect x="14" y="16" width="52" height="42" rx="14" fill="#fff" stroke="#0B3A50" strokeWidth="2.5" />
            <rect x="20" y="26" width="40" height="18" rx="6" fill="#0B3A50" />
            <circle cx="30" cy="35" r="3" fill="#5DD4FF" />
            <circle cx="50" cy="35" r="3" fill="#5DD4FF" />
            <circle cx="14" cy="38" r="3" fill="#0B3A50" />
            <circle cx="66" cy="38" r="3" fill="#0B3A50" />
            <line x1="32" y1="51" x2="48" y2="51" stroke="#0B3A50" strokeWidth="2" strokeLinecap="round" />
            <rect x="22" y="60" width="36" height="22" rx="6" fill="#fff" stroke="#0B3A50" strokeWidth="2.5" />
            <line x1="40" y1="66" x2="40" y2="78" stroke="#0B3A50" strokeWidth="1.5" />
          </svg>
          <span className="ag-title">AI agent</span>
          <span className="ag-sub">turn 47<br />fanning out calls</span>
        </div>

        {/* Animated arrows SVG overlay — curved tool calls fanning from agent to each lookup */}
        <svg className="la-arrows-svg" viewBox="0 0 280 360" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <marker id="laArrow1" viewBox="0 0 10 8" refX="9" refY="4" markerWidth="8" markerHeight="6" orient="auto"><path d="M0 0 L10 4 L0 8Z" fill="rgba(224,123,123,0.85)" /></marker>
            <marker id="laArrow2" viewBox="0 0 10 8" refX="9" refY="4" markerWidth="8" markerHeight="6" orient="auto"><path d="M0 0 L10 4 L0 8Z" fill="rgba(15,134,199,0.85)" /></marker>
            <marker id="laArrow3" viewBox="0 0 10 8" refX="9" refY="4" markerWidth="8" markerHeight="6" orient="auto"><path d="M0 0 L10 4 L0 8Z" fill="rgba(232,178,106,0.95)" /></marker>
            <marker id="laArrow4" viewBox="0 0 10 8" refX="9" refY="4" markerWidth="8" markerHeight="6" orient="auto"><path d="M0 0 L10 4 L0 8Z" fill="rgba(122,174,133,0.85)" /></marker>
            <marker id="laArrow5" viewBox="0 0 10 8" refX="9" refY="4" markerWidth="8" markerHeight="6" orient="auto"><path d="M0 0 L10 4 L0 8Z" fill="rgba(156,143,201,0.85)" /></marker>
          </defs>

          {/* Outgoing curved paths: all fan out from agent center (0,180) → center of each lookup box */}
          <path d="M0,180 C90,180 140,60  280,60"  fill="none" stroke="rgba(224,123,123,0.5)" strokeWidth="2" strokeDasharray="8 5" markerEnd="url(#laArrow1)" />
          <path d="M0,180 C90,180 140,128 280,128" fill="none" stroke="rgba(15,134,199,0.5)"  strokeWidth="2" strokeDasharray="8 5" markerEnd="url(#laArrow2)" />
          <path d="M0,180 C90,180 190,196 280,196" fill="none" stroke="rgba(232,178,106,0.6)"  strokeWidth="2" strokeDasharray="8 5" markerEnd="url(#laArrow3)" />
          <path d="M0,180 C90,180 140,264 280,264" fill="none" stroke="rgba(122,174,133,0.5)"  strokeWidth="2" strokeDasharray="8 5" markerEnd="url(#laArrow4)" />
          <path d="M0,180 C90,180 140,332 280,332" fill="none" stroke="rgba(156,143,201,0.5)"  strokeWidth="2" strokeDasharray="8 5" markerEnd="url(#laArrow5)" />

          {/* Return paths (slightly offset below each outgoing endpoint) */}
          <path d="M280,74  C140,74  90,184 0,184" fill="none" stroke="rgba(224,123,123,0.15)" strokeWidth="1.5" strokeDasharray="4 6" />
          <path d="M280,142 C140,142 90,186 0,186" fill="none" stroke="rgba(15,134,199,0.15)"  strokeWidth="1.5" strokeDasharray="4 6" />
          <path d="M280,210 C190,210 90,188 0,188" fill="none" stroke="rgba(232,178,106,0.15)"  strokeWidth="1.5" strokeDasharray="4 6" />
          <path d="M280,278 C140,278 90,190 0,190" fill="none" stroke="rgba(122,174,133,0.15)"  strokeWidth="1.5" strokeDasharray="4 6" />
          <path d="M280,346 C140,346 90,192 0,192" fill="none" stroke="rgba(156,143,201,0.15)"  strokeWidth="1.5" strokeDasharray="4 6" />

          {/* Animated token boxes following curved return paths */}
          {/* k1 — rose — clicks_stream */}
          <g><rect x="-9" y="-6" width="18" height="12" rx="3" fill="rgba(224,123,123,0.9)" />
            <animateMotion path="M280,74 C140,74 90,184 0,184" dur="3.2s" repeatCount="indefinite" rotate="auto" /></g>
          <g><rect x="-7" y="-6" width="14" height="12" rx="3" fill="rgba(224,123,123,0.6)" />
            <animateMotion path="M280,74 C140,74 90,184 0,184" dur="3.2s" begin="0.5s" repeatCount="indefinite" rotate="auto" /></g>

          {/* k2 — cyan — purchases_stream */}
          <g><rect x="-8" y="-6" width="16" height="12" rx="3" fill="rgba(15,134,199,0.9)" />
            <animateMotion path="M280,142 C140,142 90,186 0,186" dur="4.0s" repeatCount="indefinite" rotate="auto" /></g>
          <g><rect x="-6" y="-6" width="12" height="12" rx="3" fill="rgba(15,134,199,0.6)" />
            <animateMotion path="M280,142 C140,142 90,186 0,186" dur="4.0s" begin="0.6s" repeatCount="indefinite" rotate="auto" /></g>

          {/* k3 — warm — product_catalog */}
          <g><rect x="-10" y="-6" width="20" height="12" rx="3" fill="rgba(232,178,106,0.95)" />
            <animateMotion path="M280,210 C190,210 90,188 0,188" dur="2.4s" repeatCount="indefinite" rotate="auto" /></g>
          <g><rect x="-7" y="-6" width="14" height="12" rx="3" fill="rgba(232,178,106,0.6)" />
            <animateMotion path="M280,210 C190,210 90,188 0,188" dur="2.4s" begin="0.4s" repeatCount="indefinite" rotate="auto" /></g>

          {/* k4 — sage — inventory_levels */}
          <g><rect x="-8" y="-6" width="16" height="12" rx="3" fill="rgba(122,174,133,0.9)" />
            <animateMotion path="M280,278 C140,278 90,190 0,190" dur="4.8s" repeatCount="indefinite" rotate="auto" /></g>
          <g><rect x="-6" y="-6" width="12" height="12" rx="3" fill="rgba(122,174,133,0.6)" />
            <animateMotion path="M280,278 C140,278 90,190 0,190" dur="4.8s" begin="0.7s" repeatCount="indefinite" rotate="auto" /></g>

          {/* k5 — lilac — user_profile */}
          <g><rect x="-9" y="-6" width="18" height="12" rx="3" fill="rgba(156,143,201,0.9)" />
            <animateMotion path="M280,346 C140,346 90,192 0,192" dur="2.8s" repeatCount="indefinite" rotate="auto" /></g>
          <g><rect x="-7" y="-6" width="14" height="12" rx="3" fill="rgba(156,143,201,0.6)" />
            <animateMotion path="M280,346 C140,346 90,192 0,192" dur="2.8s" begin="0.5s" repeatCount="indefinite" rotate="auto" /></g>
        </svg>

        {/* Lookups */}
        <div className="la-left">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="lab">Per-token lookups</span>
            <span className="la-parallel-tag">in parallel</span>
          </div>
          <div className="lookup-grid">
            <div className="lookup k1">
              <span className="ltype">stream tail</span>
              <span className="lname">clicks_stream</span>
              <span className="ldur"><span className="pulse" />last 5m · 38ms</span>
            </div>
            <div className="lookup k2">
              <span className="ltype">api call</span>
              <span className="lname">purchases_stream</span>
              <span className="ldur"><span className="pulse" />last order · 64ms</span>
            </div>
            <div className="lookup k3">
              <span className="ltype">kv lookup</span>
              <span className="lname">product_catalog</span>
              <span className="ldur"><span className="pulse" />SKU=AX-9 · 12ms</span>
            </div>
            <div className="lookup k4">
              <span className="ltype">tool call</span>
              <span className="lname">inventory_levels</span>
              <span className="ldur"><span className="pulse" />warehouse · 88ms</span>
            </div>
            <div className="lookup k5">
              <span className="ltype">cdc fetch</span>
              <span className="lname">user_profile</span>
              <span className="ldur"><span className="pulse" />id=u_847 · 18ms</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 16 — Challenge #2: State Management
   ══════════════════════════════════════════════════════════════════════ */

const Slide16 = () => (
  <div className="slide-body design-slide">
    <div className="frame" style={{ gap: 'var(--gap-title)' }}>
      <div>
        <h1 className="title">Challenge #2: <em>State Management</em></h1>
        <p className="s3-intro" style={{ maxWidth: 1400 }}>
          Intent lives in sequences, not single events. Agents are stateless by default &mdash;
          we have to give them a <strong>window of time</strong> to reason over.
        </p>
      </div>
      <div className="body-text" style={{ maxWidth: 1400 }}>
        <p>
          Useful signal: <strong>&ldquo;this user has looked at five winter jackets in the last hour.&rdquo;</strong>
          {' '}Not useful: &ldquo;this user just clicked.&rdquo;
        </p>
        <p style={{ marginTop: 24, opacity: 0.7, fontStyle: 'italic' }}>
          Two patterns ahead. <strong>The Window:</strong> how to summarize a time range.{' '}
          <strong>The Compaction:</strong> how to prune what reaches the agent.
        </p>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 17 — Eager vs Lazy: The Window
   ══════════════════════════════════════════════════════════════════════ */

const Slide17 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>Eager vs. Lazy: <em style={S.accent}>The Window</em></h2>
    <div style={S.twoCol}>
      {/* Eager */}
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tagGreen, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>EAGER (FLINK SQL)</div>
        <p style={{ fontSize: '1.45rem', opacity: 0.7, margin: 0 }}>
          A hopping window aggregates intent every five minutes.
        </p>
        <pre style={{ ...S.code, fontSize: '1.1rem', padding: '1.25rem 1.5rem' }}><code>{`SELECT user_id,
       COUNT(*) AS total_clicks,
       LISTAGG(action, ',')
         AS history
FROM   clicks_stream
WINDOW HOP(
  ts,
  INTERVAL '5' MINUTE,
  INTERVAL '1' HOUR
)
GROUP BY user_id`}</code></pre>
      </div>
      {/* Lazy */}
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tagOrange, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>LAZY (ORCA PYTHON)</div>
        <p style={{ fontSize: '1.45rem', opacity: 0.7, margin: 0 }}>
          The agent range-reads the last hour from the state store.
        </p>
        <pre style={{ ...S.code, fontSize: '1.1rem', padding: '1.25rem 1.5rem' }}><code>{`history = self.state.query(
    'user_activity',
    key=user_id,
    time_range='1h'
)

prompt = build(event, history)
return self.agent.run(prompt)`}</code></pre>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 18 — The Lossy Compression Trap
   ══════════════════════════════════════════════════════════════════════ */

const Slide18 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h2 style={S.h2}>The <em style={S.accent}>Lossy Compression</em> Trap</h2>
    <p style={{ ...S.body, fontSize: '1.5rem', lineHeight: 1.5 }}>
      Eager summaries can over-compress. If you turn 1,000 clicks into{' '}
      <code style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.1)', padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '1.3rem' }}>
        click_count: 1000
      </code>, the agent knows the volume &mdash; but loses the velocity and the intent.
    </p>
    <p style={{ ...S.body, fontSize: '1.5rem', lineHeight: 1.5 }}>
      The high-signal events were the <em>LAST THREE</em> clicks on the &ldquo;Compare&rdquo; button.
      You compressed those away to save tokens. The agent confidently reasons over a number when
      the truth was in the order.
    </p>
    <div style={{ ...S.card, background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}>
      <div style={{ ...S.tagGreen, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>FIX</div>
      <p style={{ ...S.body, fontSize: '1.3rem', lineHeight: 1.5, marginTop: '0.5rem' }}>
        <strong>Tiered windowing.</strong> Keep recent events raw (last 15 min), summarize older
        windows (1h, 24h). The agent gets both the signal AND the trend.
      </p>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 19 — Eager vs Lazy: The Compaction
   ══════════════════════════════════════════════════════════════════════ */

const Slide19 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>Eager vs. Lazy: <em style={S.accent}>The Compaction</em></h2>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', gridTemplateRows: 'auto auto auto', alignItems: 'start' }}>
      {/* Row 1: tags */}
      <div style={{ ...S.tagGreen, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>EAGER (FLINK SQL)</div>
      <div style={{ ...S.tagOrange, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>LAZY (PYTHON)</div>
      {/* Row 2: descriptions */}
      <p style={{ fontSize: '1.45rem', opacity: 0.7, margin: 0 }}>
        Structural pruning &mdash; noise never reaches the agent.
      </p>
      <p style={{ fontSize: '1.45rem', opacity: 0.7, margin: 0 }}>
        Semantic pruning &mdash; the agent decides at reasoning time what matters for THIS task.
      </p>
      {/* Row 3: code blocks — aligned horizontally */}
      <pre style={{ ...S.code, fontSize: '1.1rem', padding: '1.25rem 1.5rem' }}><code>{`SELECT *
FROM   clicks_stream
WHERE  event_type <> 'heartbeat'
QUALIFY ROW_NUMBER() OVER (
  PARTITION BY user_id, action
  ORDER BY ts DESC
) <= 3`}</code></pre>
      <pre style={{ ...S.code, fontSize: '1.1rem', padding: '1.25rem 1.5rem' }}><code>{`clean = [
    e for e in history
    if e.type in {
        'SEARCH',
        'ADD_TO_CART',
        'COMPARE'
    }
]`}</code></pre>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 20 — Challenge #3: Observability
   ══════════════════════════════════════════════════════════════════════ */

const Slide20 = () => (
  <div className="slide-body design-slide">
    <div className="frame" style={{ gap: 'var(--gap-title)' }}>
      <div>
        <h1 className="title">Challenge #3: <em>Observability</em></h1>
        <p className="s3-intro" style={{ maxWidth: 1400 }}>
          Is the context <strong>fit for the agent?</strong>
        </p>
      </div>
      <div className="body-text" style={{ maxWidth: 1400 }}>
        <p>
          Traditional observability tells you if the pipe is leaking.
        </p>
        <p style={{ marginTop: 24 }}>
          Agent-facing observability tells you if <strong>the water is drinkable</strong>.
        </p>
        <p style={{ marginTop: 32, opacity: 0.75 }}>
          Agents fail <strong>silently</strong>. A row can be schema-valid, on-time, and complete &mdash;
          and still be contextually toxic for an LLM. Your existing dashboards won&apos;t catch it.
        </p>
        <p style={{ marginTop: 32 }}>
          An agent <strong>should</strong> write every decision back to the event bus &mdash; making every inference auditable and every failure <strong>replayable</strong>.
        </p>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 21 — Traditional Data Quality
   ══════════════════════════════════════════════════════════════════════ */

const Slide21 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h2 style={S.h2}>Why Traditional Data Quality <em style={S.accent}>Misses Agent Failures</em></h2>
    <div style={S.twoCol}>
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tagRed, fontSize: '1.2rem', padding: '0.35rem 0.9rem' }}>THE THREE PILLARS</div>
        <p style={{ ...S.body, fontSize: '1.5rem', lineHeight: 1.5, marginTop: '0.5rem' }}>
          Completeness, Validity, Volume check the <em>SHAPE</em> of the data &mdash;
          not its <em>FITNESS</em> as context.
        </p>
        <div style={{ ...S.card, marginTop: '0.5rem', padding: '1.75rem' }}>
          <div style={{ ...S.label, fontSize: '1.15rem' }}>COMPLETENESS</div>
          <p style={{ fontSize: '1.3rem', lineHeight: 1.5, margin: '0.25rem 0 0', opacity: 0.8 }}>
            <code style={{ fontFamily: 'monospace' }}>user_segment is not null</code> &mdash; passes.
            But if the windowing job lagged, only 10 of 50 sessions are in the window.
          </p>
        </div>
      </div>
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tagGreen, fontSize: '1.2rem', padding: '0.35rem 0.9rem' }}>WHAT AGENTS NEED</div>
        <p style={{ ...S.body, fontSize: '1.5rem', lineHeight: 1.5, marginTop: '0.5rem' }}>
          Fitness measured at the <em>assembled-context level</em>, not at the source-topic level.
        </p>
        <div style={{ ...S.card, marginTop: '0.5rem', padding: '1.75rem' }}>
          <div style={{ ...S.label, fontSize: '1.15rem' }}>VOLUME</div>
          <p style={{ fontSize: '1.3rem', lineHeight: 1.5, margin: '0.25rem 0 0', opacity: 0.8 }}>
            Spike alerts fire on raw event count. But heartbeats spiking is not a volume
            problem &mdash; it&apos;s a <em>signal-to-noise</em> problem.
          </p>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 22 — Agentic Four Metrics
   ══════════════════════════════════════════════════════════════════════ */

const agenticFour = [
  {
    name: 'Token Efficiency',
    subtitle: 'Signal-to-Noise Ratio',
    body: 'What percentage of the context provided was actually cited or used by the model?',
    slo: '> 60% citation rate per inference. Below this, you\'re burning tokens on noise.',
  },
  {
    name: 'Semantic Drift',
    subtitle: 'Summary Fidelity',
    body: 'Is the streaming summary still a faithful representation of the raw log? Periodic small-model audits.',
    slo: 'drift score < 0.15 (cosine distance between summary embedding and source).',
  },
  {
    name: 'Context Staleness',
    subtitle: 'World-State Latency',
    body: 'Delta between source event and assembled context.',
    slo: 'P99 freshness < 500ms end-to-end. Coherence skew < 5s across joined streams.',
  },
  {
    name: 'Reference Integrity',
    subtitle: 'Entity Consistency',
    body: 'When the agent cites entity IDs, do those IDs actually exist in the source-of-truth topic? Async back-checking.',
    slo: '> 99.5% of assembled contexts include all required streams within the freshness budget.',
  },
];

const Slide22 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 56px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>The Agentic Four: <em style={S.accent}>Metrics and SLO Targets</em></h2>
    <div style={S.fourGrid}>
      {agenticFour.map((m, i) => (
        <div key={i} style={{ ...S.card, padding: '2rem' }}>
          <h3 style={{ ...S.h3, fontSize: '1.59rem' }}>{m.name}</h3>
          <div style={{ ...S.tag, fontSize: '1.14rem', marginBottom: '0.5rem' }}>{m.subtitle}</div>
          <p style={{ fontSize: '1.26rem', lineHeight: 1.5, opacity: 0.85, margin: '0 0 0.75rem' }}>{m.body}</p>
          <div style={{ fontSize: '1.22rem', padding: '0.4rem 0.6rem', background: 'rgba(24,143,255,0.1)', borderRadius: '6px', fontWeight: 600 }}>
            SLO: {m.slo}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 23 — How to Measure
   ══════════════════════════════════════════════════════════════════════ */

const measureCards = [
  {
    name: 'Token Efficiency',
    where: 'async post-inference job',
    how: 'Read the context debug log. For each inference, compare assembled context chunks against the agent\'s output using embedding similarity. Chunks below threshold = unused tokens.',
  },
  {
    name: 'Semantic Drift',
    where: 'periodic audit job (hourly)',
    how: 'Sample N summaries/hour. Retrieve raw events from the source topic. Embed both. Cosine distance = drift score.',
  },
  {
    name: 'Context Staleness',
    where: 'Flink job on the assembled context topic',
    how: 'Every context record carries assembled_at + source_timestamps. Freshness = assembled_at − min(source_ts).',
  },
  {
    name: 'Reference Integrity',
    where: 'two places',
    how: 'Assembly completeness: counter on the Flink join — did all required LEFT JOINs return non-null? Citation integrity: async consumer on agent_actions topic.',
  },
];

const Slide23 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0 }}>
    <h2 style={S.h2}>How to Measure: <em style={S.accent}>Where Each Metric Lives</em></h2>
    <div style={S.fourGrid}>
      {measureCards.map((m, i) => (
        <div key={i} style={{ ...S.card, padding: '2rem' }}>
          <h3 style={{ ...S.h3, fontSize: '1.44rem' }}>{m.name}</h3>
          <div style={{ ...S.tag, fontSize: '1.14rem' }}>WHERE: {m.where}</div>
          <div style={{ ...S.label, fontSize: '1.19rem', marginTop: '0.75rem' }}>HOW</div>
          <p style={{ fontSize: '1.24rem', lineHeight: 1.5, opacity: 0.85, margin: '0.25rem 0 0' }}>{m.how}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 24 — Context Debug Log
   ══════════════════════════════════════════════════════════════════════ */

const Slide24 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h2 style={S.h2}>Build a <em style={S.accent}>Context Debug Log</em></h2>
    <p style={{ ...S.body, fontSize: '1.2rem' }}>
      Every inference, log the full assembled prompt, the sources that contributed to it,
      the latency for each source, and the citations the model returned.
    </p>
    <pre style={{ ...S.code, fontSize: '1.1rem', padding: '1.25rem 1.5rem' }}><code>{`{
  "inference_id": "inf_8a3f...",
  "prompt": "<full assembled text>",
  "sources": [
    "clicks_stream", "product_catalog",
    "user_profile", "inventory_levels"
  ],
  "latencies_ms": {
    "clicks_stream": 38,
    "product_catalog": 12,
    "user_profile": 18,
    "inventory_levels": 88
  },
  "citations": [
    "product_catalog:SKU-AX9",
    "user_profile:u_847"
  ]
}`}</code></pre>
    <p style={{ ...S.body, fontSize: '1.1rem', opacity: 0.6 }}>
      Typically 3&ndash;10 KB per inference.
    </p>
    <p style={{ ...S.body, fontSize: '1.2rem', fontStyle: 'italic' }}>
      In traditional data, you log the query. In agentic systems, you log the <em>assembled context</em>.
    </p>
    <p style={{ ...S.body, fontSize: '1.2rem' }}>
      <strong>Persistent streaming memory</strong> takes this further. The agent externalizes every decision as an event on the bus.
      When something goes wrong, you don&apos;t just read the log &mdash; you <strong>replay the exact event sequence</strong> through
      the agent and reproduce the failure deterministically.
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 25 — Three Failure Modes
   ══════════════════════════════════════════════════════════════════════ */

const failureModes = [
  {
    name: 'Temporal Tearing',
    body: 'Fast purchase stream beats slow user_profile CDC. Join emits a purchase for a user "who doesn\'t exist."',
    fix: 'Interval joins or temporal tables on the slow side.',
  },
  {
    name: 'Context Echo',
    body: 'The agent\'s own actions become events in its input stream. Feedback loop: ten discount codes in five minutes.',
    fix: 'Source filtering on actor_id.',
  },
  {
    name: 'Laggard Problem',
    body: 'Eager freshness equals the slowest stream\'s latency plus your watermark slack. Don\'t tune for speed; tune for SLACK.',
    fix: 'Pick deliberately.',
  },
];

const Slide25 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h2 style={S.h2}>Three Failure Modes <em style={S.accent}>Worth Knowing By Name</em></h2>
    <div style={S.threeCol}>
      {failureModes.map((f, i) => (
        <div key={i} style={{ ...S.card, padding: '2rem' }}>
          <h3 style={{ ...S.h3, fontSize: '1.6rem' }}>{f.name}</h3>
          <p style={{ fontSize: '1.35rem', lineHeight: 1.45, opacity: 0.85, margin: '0 0 0.75rem' }}>{f.body}</p>
          <div style={{ ...S.card, padding: '0.8rem 1rem', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}>
            <div style={{ ...S.label, fontSize: '1.05rem' }}>FIX</div>
            <p style={{ fontSize: '1.25rem', margin: '0.15rem 0 0', opacity: 0.85 }}>{f.fix}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 26 — Backpressure
   ══════════════════════════════════════════════════════════════════════ */

const Slide26 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h2 style={S.h2}>Backpressure: <em style={S.accent}>The Context Window as Flow Control</em></h2>
    <div style={{ ...S.twoCol, flex: 1 }}>
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tag, fontSize: '1.2rem', padding: '0.35rem 0.9rem' }}>THE SIGNAL</div>
        <p style={{ ...S.body, fontSize: '1.5rem' }}>
          The context window isn&apos;t just a limit &mdash; it&apos;s a <em>backpressure signal</em>{' '}
          from the agent back to your pipeline. When assembled context exceeds the token budget,
          something gets cut. If your pipeline doesn&apos;t know about the budget, the cut is random.
        </p>
      </div>
      <div style={{ flex: 1, ...S.flexCol }}>
        <div style={{ ...S.tag, fontSize: '1.2rem', padding: '0.35rem 0.9rem' }}>THE RESPONSE</div>
        <p style={{ ...S.body, fontSize: '1.5rem', marginBottom: '0.75rem' }}>
          Three strategies &mdash; choose in the pipeline, not the prompt:
        </p>
        <div style={{ ...S.flexCol, gap: '0.6rem', paddingLeft: '0.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', fontSize: '1.4rem' }}>
            <span style={{ color: '#0070D9', fontSize: '1.6rem', lineHeight: 1 }}>•</span>
            <span><strong>Truncate:</strong> drop lowest-priority sources.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', fontSize: '1.4rem' }}>
            <span style={{ color: '#0070D9', fontSize: '1.6rem', lineHeight: 1 }}>•</span>
            <span><strong>Summarize:</strong> compress verbose sources mid-stream.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', fontSize: '1.4rem' }}>
            <span style={{ color: '#0070D9', fontSize: '1.6rem', lineHeight: 1 }}>•</span>
            <span><strong>Defer:</strong> hold low-priority context in a sidecar topic.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 27 — Graceful Degradation
   ══════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════
   Slide 27 — Three Delivery Models: Push / Query / Pull
   ══════════════════════════════════════════════════════════════════════ */

const Slide27 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1rem' }}>
    <h2 style={S.h2}>Three Delivery Models: <em style={S.accent}>Push · Query · Pull</em></h2>
    {/* Single grid: 3 columns × 4 rows — guarantees horizontal alignment */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: 'auto auto auto auto', gap: '0 1.5rem' }}>

      {/* Row 1: Tags */}
      <div style={{ ...S.card, padding: '1.5rem 1.5rem 0', borderWidth: '3px', borderBottom: 'none', borderRadius: '12px 12px 0 0', borderColor: 'rgba(52,211,153,0.25)' }}>
        <div style={{ ...S.tagGreen, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>PUSH</div>
      </div>
      <div style={{ ...S.card, padding: '1.5rem 1.5rem 0', borderWidth: '3px', borderBottom: 'none', borderRadius: '12px 12px 0 0', borderColor: 'rgba(24,143,255,0.25)' }}>
        <div style={{ ...S.tag, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>QUERY</div>
      </div>
      <div style={{ ...S.card, padding: '1.5rem 1.5rem 0', borderWidth: '3px', borderBottom: 'none', borderRadius: '12px 12px 0 0', borderColor: 'rgba(251,191,36,0.25)' }}>
        <div style={{ ...S.tagOrange, fontSize: '1.1rem', padding: '0.35rem 0.9rem' }}>PULL</div>
      </div>

      {/* Row 2: SVG diagrams */}
      <div style={{ ...S.card, padding: '0 1.5rem', borderWidth: '3px', borderTop: 'none', borderBottom: 'none', borderRadius: 0, borderColor: 'rgba(52,211,153,0.25)' }}>
        <svg viewBox="0 0 260 120" style={{ width: 'calc(100% + 24px)', height: '140px', margin: '0 -12px' }} xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="35" width="60" height="50" rx="6" fill="rgba(52,211,153,0.15)" stroke="#34d399" strokeWidth="1.5" />
          <text x="35" y="65" textAnchor="middle" fill="#34d399" fontSize="11" fontWeight="700">Flink</text>
          <rect x="100" y="35" width="60" height="50" rx="6" fill="rgba(52,211,153,0.08)" stroke="#34d399" strokeWidth="1" />
          <text x="130" y="58" textAnchor="middle" fill="#34d399" fontSize="9" fontWeight="600">topic</text>
          <text x="130" y="72" textAnchor="middle" fill="rgba(52,211,153,0.6)" fontSize="8">pre-joined</text>
          <rect x="195" y="35" width="60" height="50" rx="6" fill="rgba(52,211,153,0.15)" stroke="#34d399" strokeWidth="1.5" />
          <text x="225" y="58" textAnchor="middle" fill="#34d399" fontSize="10" fontWeight="600">🤖</text>
          <text x="225" y="72" textAnchor="middle" fill="#34d399" fontSize="9">Agent</text>
          <line x1="67" y1="60" x2="98" y2="60" stroke="#34d399" strokeWidth="1.5" markerEnd="url(#pushArrow)" />
          <line x1="162" y1="60" x2="193" y2="60" stroke="#34d399" strokeWidth="1.5" markerEnd="url(#pushArrow)" />
          <defs><marker id="pushArrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><path d="M0,0 L6,2 L0,4" fill="#34d399" /></marker></defs>
          <rect width="14" height="8" rx="2" fill="#34d399" opacity="0.8"><animateMotion path="M70,56 L195,56" dur="2s" repeatCount="indefinite" /></rect>
          <rect width="10" height="8" rx="2" fill="#34d399" opacity="0.5"><animateMotion path="M70,56 L195,56" dur="2s" begin="0.7s" repeatCount="indefinite" /></rect>
          <rect width="12" height="8" rx="2" fill="#34d399" opacity="0.6"><animateMotion path="M70,56 L195,56" dur="2s" begin="1.3s" repeatCount="indefinite" /></rect>
        </svg>
      </div>
      <div style={{ ...S.card, padding: '0 1.5rem', borderWidth: '3px', borderTop: 'none', borderBottom: 'none', borderRadius: 0, borderColor: 'rgba(24,143,255,0.25)' }}>
        <svg viewBox="0 0 260 120" style={{ width: 'calc(100% + 24px)', height: '140px', margin: '0 -12px' }} xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="25" width="110" height="70" rx="6" fill="rgba(24,143,255,0.08)" stroke="#0070D9" strokeWidth="1" />
          <text x="60" y="45" textAnchor="middle" fill="#0070D9" fontSize="9" fontWeight="700">LIVE TABLE</text>
          <line x1="15" y1="55" x2="105" y2="55" stroke="rgba(24,143,255,0.2)" strokeWidth="0.5" />
          <rect x="15" y="58" width="90" height="7" rx="2" fill="rgba(24,143,255,0.08)" />
          <line x1="15" y1="68" x2="105" y2="68" stroke="rgba(24,143,255,0.2)" strokeWidth="0.5" />
          <rect x="15" y="71" width="90" height="7" rx="2" fill="rgba(24,143,255,0.15)" stroke="#0070D9" strokeWidth="0.5" />
          <line x1="15" y1="81" x2="105" y2="81" stroke="rgba(24,143,255,0.2)" strokeWidth="0.5" />
          <rect x="15" y="84" width="90" height="7" rx="2" fill="rgba(24,143,255,0.08)" />
          <rect x="195" y="35" width="60" height="50" rx="6" fill="rgba(24,143,255,0.15)" stroke="#0070D9" strokeWidth="1.5" />
          <text x="225" y="58" textAnchor="middle" fill="#0070D9" fontSize="10" fontWeight="600">🤖</text>
          <text x="225" y="72" textAnchor="middle" fill="#0070D9" fontSize="9">Agent</text>
          <path d="M193,52 L120,52" fill="none" stroke="#0070D9" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#queryArrow)" />
          <text x="155" y="46" textAnchor="middle" fill="#0070D9" fontSize="7" fontWeight="600">query</text>
          <path d="M120,68 L193,68" fill="none" stroke="#0070D9" strokeWidth="1.5" markerEnd="url(#queryArrow)" />
          <text x="155" y="80" textAnchor="middle" fill="#0070D9" fontSize="7" fontWeight="600">filtered rows</text>
          <defs><marker id="queryArrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><path d="M0,0 L6,2 L0,4" fill="#0070D9" /></marker></defs>
          <circle r="3" fill="#0070D9" opacity="0.7"><animateMotion path="M193,52 L120,52" dur="1.5s" repeatCount="indefinite" /></circle>
          <rect width="20" height="6" rx="2" fill="#0070D9" opacity="0.6"><animateMotion path="M120,68 L193,68" dur="1.5s" begin="0.8s" repeatCount="indefinite" /></rect>
        </svg>
      </div>
      <div style={{ ...S.card, padding: '0 1.5rem', borderWidth: '3px', borderTop: 'none', borderBottom: 'none', borderRadius: 0, borderColor: 'rgba(251,191,36,0.25)' }}>
        <svg viewBox="0 0 260 120" style={{ width: 'calc(100% + 24px)', height: '140px', margin: '0 -12px' }} xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="35" width="60" height="50" rx="6" fill="rgba(251,191,36,0.15)" stroke="#fbbf24" strokeWidth="1.5" />
          <text x="35" y="58" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="600">🤖</text>
          <text x="35" y="72" textAnchor="middle" fill="#fbbf24" fontSize="9">Agent</text>
          <rect x="165" y="8" width="90" height="26" rx="4" fill="rgba(251,191,36,0.08)" stroke="#fbbf24" strokeWidth="0.8" />
          <text x="210" y="25" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="600">API · weather</text>
          <rect x="165" y="42" width="90" height="26" rx="4" fill="rgba(251,191,36,0.08)" stroke="#fbbf24" strokeWidth="0.8" />
          <text x="210" y="59" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="600">State store</text>
          <rect x="165" y="76" width="90" height="26" rx="4" fill="rgba(251,191,36,0.08)" stroke="#fbbf24" strokeWidth="0.8" />
          <text x="210" y="93" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="600">CDC · profiles</text>
          <path d="M67,50 C100,50 130,21 163,21" fill="none" stroke="rgba(251,191,36,0.5)" strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#pullArrow)" />
          <path d="M67,60 C100,60 130,55 163,55" fill="none" stroke="rgba(251,191,36,0.5)" strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#pullArrow)" />
          <path d="M67,70 C100,70 130,89 163,89" fill="none" stroke="rgba(251,191,36,0.5)" strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#pullArrow)" />
          <defs><marker id="pullArrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><path d="M0,0 L6,2 L0,4" fill="#fbbf24" /></marker></defs>
          <rect width="10" height="6" rx="2" fill="#fbbf24" opacity="0.6"><animateMotion path="M163,21 C130,21 100,50 67,50" dur="2.2s" begin="0.3s" repeatCount="indefinite" /></rect>
          <rect width="10" height="6" rx="2" fill="#fbbf24" opacity="0.6"><animateMotion path="M163,55 C130,55 100,60 67,60" dur="1.8s" begin="0.1s" repeatCount="indefinite" /></rect>
          <rect width="10" height="6" rx="2" fill="#fbbf24" opacity="0.6"><animateMotion path="M163,89 C130,89 100,70 67,70" dur="2.5s" begin="0.6s" repeatCount="indefinite" /></rect>
        </svg>
      </div>

      {/* Row 3: Code blocks — all in same grid row = guaranteed alignment */}
      <div style={{ ...S.card, padding: '48px 1.5rem 0', borderWidth: '3px', borderTop: 'none', borderBottom: 'none', borderRadius: 0, borderColor: 'rgba(52,211,153,0.25)' }}>
        <pre style={{ ...S.code, fontSize: '0.84rem', padding: '0.6rem 0.8rem', margin: 0, lineHeight: 1.5 }}><code>{`@topic_consumer("ctx")
def on_context(ctx):
  result = agent.run(ctx)
  # ~5ms — pre-assembled`}</code></pre>
      </div>
      <div style={{ ...S.card, padding: '48px 1.5rem 0', borderWidth: '3px', borderTop: 'none', borderBottom: 'none', borderRadius: 0, borderColor: 'rgba(24,143,255,0.25)' }}>
        <pre style={{ ...S.code, fontSize: '0.84rem', padding: '0.6rem 0.8rem', margin: 0, lineHeight: 1.5 }}><code>{`SELECT * FROM ctx_table
WHERE user_id = ?
  AND ts > now() - '1h'
  -- ~15ms — live table`}</code></pre>
      </div>
      <div style={{ ...S.card, padding: '48px 1.5rem 0', borderWidth: '3px', borderTop: 'none', borderBottom: 'none', borderRadius: 0, borderColor: 'rgba(251,191,36,0.25)' }}>
        <pre style={{ ...S.code, fontSize: '0.84rem', padding: '0.6rem 0.8rem', margin: 0, lineHeight: 1.5 }}><code>{`profile = tools.lookup(uid)
weather = tools.weather(zip)
stock   = tools.inventory(pid)
# ~120ms — parallel calls`}</code></pre>
      </div>

      {/* Row 4: Bottom labels */}
      <div style={{ ...S.card, padding: '96px 1.5rem 1.5rem', borderWidth: '3px', borderTop: 'none', borderRadius: '0 0 12px 12px', borderColor: 'rgba(52,211,153,0.25)', display: 'flex', alignItems: 'flex-end' }}>
        <p style={{ fontSize: '0.97rem', opacity: 0.6, margin: 0, textAlign: 'center' as const, width: '100%' }}>
          Always on · Fixed cost · Lowest latency
        </p>
      </div>
      <div style={{ ...S.card, padding: '96px 1.5rem 1.5rem', borderWidth: '3px', borderTop: 'none', borderRadius: '0 0 12px 12px', borderColor: 'rgba(24,143,255,0.25)', display: 'flex', alignItems: 'flex-end' }}>
        <p style={{ fontSize: '0.97rem', opacity: 0.6, margin: 0, textAlign: 'center' as const, width: '100%' }}>
          On-demand · Flexible reads · No external DB
        </p>
      </div>
      <div style={{ ...S.card, padding: '96px 1.5rem 1.5rem', borderWidth: '3px', borderTop: 'none', borderRadius: '0 0 12px 12px', borderColor: 'rgba(251,191,36,0.25)', display: 'flex', alignItems: 'flex-end' }}>
        <p style={{ fontSize: '0.97rem', opacity: 0.6, margin: 0, textAlign: 'center' as const, width: '100%' }}>
          On-demand · Most flexible · Highest latency
        </p>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 28 — When Eager Breaks
   ══════════════════════════════════════════════════════════════════════ */

const Slide28 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.5rem' }}>
    <h2 style={S.h2}>When Eager Breaks: <em style={S.accent}>External APIs</em></h2>
    <p style={{ ...S.body, fontSize: '1.9rem', lineHeight: 1.55 }}>
      Our agent has worked beautifully so far. Now the customer asks:{' '}
      <em>&ldquo;should I bring a jacket?&rdquo;</em> We need real-time weather.
      Weather isn&apos;t in any of our topics. We&apos;ve hit Eager&apos;s ceiling.
    </p>
    <p style={{ ...S.body, fontSize: '1.9rem', lineHeight: 1.55 }}>
      <strong>Cardinality and volatility:</strong> per-user location, unbounded by our domain &mdash;
      we can&apos;t pre-join the weather of every (lat, lon) cell to every user. And it changes
      minute by minute; any pre-computed value is immediately stale.
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 29 — Hybrid Architecture (three lanes)
   ══════════════════════════════════════════════════════════════════════ */

const Slide29 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1rem' }}>
    <h2 style={S.h2}>The Production Answer: <em style={S.accent}>Hybrid</em></h2>
    {/* Three horizontal lanes feeding into the agent */}
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
      {/* ── PUSH lane ── */}
      <div style={{ ...S.card, padding: '0.9rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.25rem', flex: 1, borderLeft: '4px solid #34d399' }}>
        <div style={{ minWidth: '70px' }}>
          <div style={{ ...S.tagGreen, fontSize: '1.14rem', padding: '0.25rem 0.7rem' }}>PUSH</div>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ ...S.body, fontSize: '1.34rem', margin: 0 }}>
            <strong>The critical five</strong> &mdash; user profile, tier, current product, inventory, browsing summary
          </p>
          <p style={{ fontSize: '1.04rem', opacity: 0.5, margin: '0.2rem 0 0' }}>Predictable · High-frequency · Pre-joined by Flink</p>
        </div>
        {/* Mini animated flow */}
        <svg viewBox="0 0 140 30" style={{ width: '192px', height: '51px', flexShrink: 0 }} xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="8" width="50" height="14" rx="3" fill="rgba(52,211,153,0.1)" stroke="#34d399" strokeWidth="0.8" />
          <text x="25" y="19" textAnchor="middle" fill="#34d399" fontSize="7" fontWeight="600">topic</text>
          <line x1="52" y1="15" x2="85" y2="15" stroke="#34d399" strokeWidth="1" />
          <rect x="88" y="5" width="48" height="20" rx="4" fill="rgba(52,211,153,0.15)" stroke="#34d399" strokeWidth="1" />
          <text x="112" y="18" textAnchor="middle" fill="#34d399" fontSize="8" fontWeight="700">🤖</text>
          <rect width="8" height="5" rx="1.5" fill="#34d399" opacity="0.7"><animateMotion path="M52,12 L88,12" dur="1.5s" repeatCount="indefinite" /></rect>
          <rect width="6" height="5" rx="1.5" fill="#34d399" opacity="0.4"><animateMotion path="M52,12 L88,12" dur="1.5s" begin="0.6s" repeatCount="indefinite" /></rect>
        </svg>
        <div style={{ minWidth: '50px', textAlign: 'right' as const }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.29rem', fontWeight: 700, color: '#34d399' }}>~5ms</span>
        </div>
      </div>

      {/* ── QUERY lane ── */}
      <div style={{ ...S.card, padding: '0.9rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.25rem', flex: 1, borderLeft: '4px solid #0070D9' }}>
        <div style={{ minWidth: '70px' }}>
          <div style={{ ...S.tag, fontSize: '1.14rem', padding: '0.25rem 0.7rem' }}>QUERY</div>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ ...S.body, fontSize: '1.34rem', margin: 0 }}>
            <strong>Flexible lookups</strong> &mdash; purchase history, returns in last 30 days, size preferences
          </p>
          <p style={{ fontSize: '1.04rem', opacity: 0.5, margin: '0.2rem 0 0' }}>Filters &amp; ranges on live tables · No external DB</p>
        </div>
        {/* Mini animated query */}
        <svg viewBox="0 0 140 30" style={{ width: '192px', height: '51px', flexShrink: 0 }} xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="5" width="50" height="20" rx="3" fill="rgba(24,143,255,0.08)" stroke="#0070D9" strokeWidth="0.8" />
          <text x="25" y="19" textAnchor="middle" fill="#0070D9" fontSize="7" fontWeight="600">table</text>
          <path d="M88,11 L54,11" fill="none" stroke="#0070D9" strokeWidth="0.8" strokeDasharray="3 2" />
          <path d="M54,19 L88,19" fill="none" stroke="#0070D9" strokeWidth="1" />
          <rect x="88" y="5" width="48" height="20" rx="4" fill="rgba(24,143,255,0.15)" stroke="#0070D9" strokeWidth="1" />
          <text x="112" y="18" textAnchor="middle" fill="#0070D9" fontSize="8" fontWeight="700">🤖</text>
          <circle r="2" fill="#0070D9" opacity="0.7"><animateMotion path="M88,11 L54,11" dur="1.2s" repeatCount="indefinite" /></circle>
          <rect width="12" height="4" rx="1.5" fill="#0070D9" opacity="0.6"><animateMotion path="M54,19 L88,19" dur="1.2s" begin="0.6s" repeatCount="indefinite" /></rect>
        </svg>
        <div style={{ minWidth: '50px', textAlign: 'right' as const }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.29rem', fontWeight: 700, color: '#0070D9' }}>~15ms</span>
        </div>
      </div>

      {/* ── PULL lane ── */}
      <div style={{ ...S.card, padding: '0.9rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.25rem', flex: 1, borderLeft: '4px solid #fbbf24' }}>
        <div style={{ minWidth: '70px' }}>
          <div style={{ ...S.tagOrange, fontSize: '1.14rem', padding: '0.25rem 0.7rem' }}>PULL</div>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ ...S.body, fontSize: '1.34rem', margin: 0 }}>
            <strong>External &amp; volatile</strong> &mdash; weather, competitor pricing, external reviews, cross-sell recs
          </p>
          <p style={{ fontSize: '1.04rem', opacity: 0.5, margin: '0.2rem 0 0' }}>Unpredictable · API round trips · On-demand only</p>
        </div>
        {/* Mini animated fan-out */}
        <svg viewBox="0 0 140 30" style={{ width: '192px', height: '51px', flexShrink: 0 }} xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="3" width="30" height="10" rx="2" fill="rgba(251,191,36,0.08)" stroke="#fbbf24" strokeWidth="0.6" />
          <rect x="0" y="17" width="30" height="10" rx="2" fill="rgba(251,191,36,0.08)" stroke="#fbbf24" strokeWidth="0.6" />
          <text x="15" y="11" textAnchor="middle" fill="#fbbf24" fontSize="5" fontWeight="600">API</text>
          <text x="15" y="25" textAnchor="middle" fill="#fbbf24" fontSize="5" fontWeight="600">API</text>
          <path d="M88,12 C65,12 45,8 32,8" fill="none" stroke="rgba(251,191,36,0.5)" strokeWidth="0.8" strokeDasharray="3 2" />
          <path d="M88,18 C65,18 45,22 32,22" fill="none" stroke="rgba(251,191,36,0.5)" strokeWidth="0.8" strokeDasharray="3 2" />
          <rect x="88" y="5" width="48" height="20" rx="4" fill="rgba(251,191,36,0.15)" stroke="#fbbf24" strokeWidth="1" />
          <text x="112" y="18" textAnchor="middle" fill="#fbbf24" fontSize="8" fontWeight="700">🤖</text>
          <rect width="6" height="4" rx="1.5" fill="#fbbf24" opacity="0.6"><animateMotion path="M32,8 C45,8 65,12 88,12" dur="2s" begin="0.4s" repeatCount="indefinite" /></rect>
          <rect width="6" height="4" rx="1.5" fill="#fbbf24" opacity="0.6"><animateMotion path="M32,22 C45,22 65,18 88,18" dur="2.4s" begin="0.8s" repeatCount="indefinite" /></rect>
        </svg>
        <div style={{ minWidth: '50px', textAlign: 'right' as const }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.29rem', fontWeight: 700, color: '#fbbf24' }}>~120ms</span>
        </div>
      </div>
    </div>
    <p style={{ ...S.body, fontSize: '1.34rem', textAlign: 'center' as const, opacity: 0.7, margin: 0 }}>
      Identify your <strong>&ldquo;critical five&rdquo;</strong> &mdash; those go Push. Flexible enrichment goes Query. Everything else goes Pull.
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 30 — Five Questions
   ══════════════════════════════════════════════════════════════════════ */

const fiveQuestions = [
  'Can you deliver an enriched context payload in under 100 ms end-to-end?',
  'Can you summarize event histories — not just store them?',
  'Can you measure SNR on assembled contexts (not just source topics)?',
  'Can you route events by intent — not just by topic?',
  'Can you trace a single inference back to every source event that contributed to its context?',
];

const Slide30 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.25rem' }}>
    <h2 style={S.h2}>Five Questions: <em style={S.accent}>Is Your Platform Ready?</em></h2>
    <p style={{ ...S.body, fontSize: '1.58rem', lineHeight: 1.5, opacity: 0.7 }}>
      Five questions to evaluate whether your data platform is ready for AI-native workloads.
      Four out of five = ready. Less than three = start with observability.
    </p>
    <div style={{ ...S.flexCol, gap: '0.75rem', flex: 1, justifyContent: 'flex-start' }}>
      {fiveQuestions.map((q, i) => (
        <div key={i} style={{ ...S.numberedItem, gap: '1.25rem' }}>
          <div style={{ ...S.numberCircle, width: '2.5rem', height: '2.5rem', minWidth: '2.5rem', fontSize: '1.15rem' }}>{i + 1}</div>
          <p style={{ ...S.body, fontSize: '1.4rem', margin: 0 }}>{q}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 31 — Closing
   ══════════════════════════════════════════════════════════════════════ */

const Slide31 = () => (
  <div className="slide-body" style={{ ...S.flexCol, position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, padding: '90px 110px 80px', maxWidth: 'none', margin: 0, gap: '1.75rem' }}>
    <h2 style={S.h1}>
      Curate <em style={S.accent}>Reality</em>
    </h2>
    <p style={{ ...S.body, fontSize: '2rem', lineHeight: 1.55 }}>
      In traditional software, the database is the source of truth.
    </p>
    <p style={{ ...S.body, fontSize: '2rem', lineHeight: 1.55 }}>
      In agentic systems, the <em style={S.accent}>context window</em> is the source of truth.
    </p>
    <p style={{ ...S.body, fontSize: '2rem', lineHeight: 1.55 }}>
      Your job &mdash; whether you&apos;re writing Flink SQL or wiring up an Orca agent &mdash;
      isn&apos;t to move data anymore. It&apos;s to <em>curate reality</em> for a reasoning engine.
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide definitions
   ══════════════════════════════════════════════════════════════════════ */

export const slides: readonly Slide[] = [
  {
    id: 1,
    section: 'Current London 2026',
    theme: 'beige',
    notes: [
      '~30s · "I\'m David Kjerrumgaard — twenty-plus years in data infrastructure, most of it in streaming with Kafka and Pulsar. This talk is about what happens when the primary consumer of your data pipelines is no longer a human — it\'s an AI agent. We\'ll follow one scenario throughout: a real-time shopping assistant that helps customers find the right product. We\'ll get into architecture patterns, code, and failure modes. If you\'re running Kafka or Flink today, you already have the building blocks. The question is how to wire them for this new kind of consumer."',
    ],
    render: Slide01,
  },
  {
    id: 2,
    section: 'Introduction',
    theme: 'dark',
    notes: [
      '~60s · "For thirty years we built pipelines for humans. An analyst waits twelve seconds for a query — fine. A dashboard refreshes every thirty minutes — no one complains. Humans are forgiving consumers."',
      '"Our shopping assistant is not forgiving. A customer clicks on a winter jacket, and the agent has 500 milliseconds to assemble context — who is this user, what did they browse, is the item in stock — before it can reason about a recommendation. If the inventory feed is stale, the agent confidently recommends a product that sold out an hour ago. It doesn\'t error. It hallucinates."',
      '"In agentic systems, the context window is the source of truth. Remember that line — we\'ll come back to it at the end."',
    ],
    render: Slide02,
  },
  {
    id: 3,
    section: 'Introduction',
    theme: 'white',
    notes: [
      '~90s · "This table is the business case for everything that follows. Six dimensions where agents break assumptions your pipelines were built on."',
      '"Latency: your analyst waits 12 seconds for a query. Your agent times out at 500ms inside a tool call. That\'s not a performance wish — it\'s a hard deadline in the inference loop."',
      '"Staleness: an analyst sees yesterday\'s numbers and adjusts mentally. The shopping assistant sees stale inventory and recommends a product that sold out an hour ago. It doesn\'t know the data is stale — it just acts on it."',
      '"Consumption — this is the row that should change how you think about your pipelines. Humans read dashboards on demand. Agents need context assembled and waiting on event arrival. Your pipeline isn\'t feeding a dashboard anymore — it\'s feeding a reasoning engine on a clock."',
      '"Failure cost — and this is the row I want you to remember. When an analyst gets bad data, they re-run the query. When our shopping assistant gets bad data, it makes a bad recommendation, the customer acts on it, and downstream agents — the order agent, the inventory agent — all inherit that mistake. Cascading failures across an agent mesh."',
      '*** TRANSITION *** "So what do we need to build? It starts with understanding what agents actually need."',
    ],
    render: Slide03,
  },
  {
    id: 4,
    section: 'Context Foundations',
    theme: 'dark',
    notes: [
      '~60s · "The answer is context. Think of the context window as the agent\'s working memory. Everything the shopping assistant knows at inference time — who the customer is, what they clicked, what\'s in stock, the system prompt, the conversation history — all of it lives inside this window."',
      '"It\'s finite, it\'s shared between input and output, and it\'s the ONLY thing the model can reason over. If a fact isn\'t in the window, it doesn\'t exist for the agent. The rest of this talk is about how to fill this window with the right data, at the right time, at the right cost."',
    ],
    render: Slide04,
  },
  {
    id: 5,
    section: 'Context Foundations',
    theme: 'dark',
    notes: [
      '~60s · "Here\'s something most Kafka engineers haven\'t had to think about: your data has a new unit of cost. Not bytes — tokens. And unlike disk or bandwidth, this resource is hard-capped. There\'s no autoscaling. Every token you waste on noise is a token the agent can\'t use for reasoning."',
      '"That sentence on the left — \'Build infrastructure for the agent era\' — is 8 tokens. A single click event might be 200 bytes but 80 tokens. A full product catalog record could be 400 tokens. Fill the window with three verbose tool responses and you\'ve burned half the budget before the agent even starts thinking about the customer\'s question."',
      '"On the right: watch how fast the space fills. System prompt, conversation history, retrieved data — 124 out of 200 tokens used. Scale that to production — 120K out of 200K — and the agent is 60% full before you inject a single fresh fact. Context is a limited resource. Every token must earn its seat."',
    ],
    render: Slide05,
  },
  {
    id: 6,
    section: 'Context Foundations',
    theme: 'white',
    notes: [
      '~75s · "Two types of content live inside the window. On the left: static content. System prompt, tool definitions, guardrails. These get loaded once at the start of the session and stay pinned. For our shopping assistant, that\'s about 6K tokens — the persona, the product recommendation tools, the return policy rules."',
      '"On the right: dynamic content. This is the stream — fresh data flowing in every turn. User messages, RAG retrievals, tool call results, conversation history. This is the part that changes every inference, and it\'s the part YOU engineer. The static content is set by the application developer. The dynamic content is set by your pipeline."',
    ],
    render: Slide06,
  },
  {
    id: 7,
    section: 'Context Foundations',
    theme: 'white',
    notes: [
      '~75s · "Three columns. Input on the left — your prompts, retrieved docs, tool results. Output in the middle — the agent\'s response, its tool calls, its reasoning. Third column — they share the same fixed budget. Input and output compete for the same space."',
      '"Let\'s do the math. 200K tokens sounds generous. Our shopping assistant is on turn 47. Conversation history: 40K tokens. System prompt: 6K. Tool definitions: 8K. The agent\'s recent reasoning: 15K. That\'s 69K tokens of overhead — before we inject a single fact about the customer. The remaining budget for fresh context is whatever\'s left, and it shrinks every turn."',
      '"This is why efficiency matters so much. If your pipeline dumps a raw 80K-token context payload into that remaining space, the agent has to truncate — and truncation is random unless you control it. The pipeline\'s job isn\'t just delivery. It\'s curation. And bigger windows aren\'t the answer — cost and latency scale linearly. 200K tokens costs roughly 4x what 50K costs per inference."',
    ],
    render: Slide07,
  },
  {
    id: 8,
    section: 'Context Foundations',
    theme: 'white',
    notes: [
      '~75s · "Context engineering is deciding what earns a seat in that window. Every turn, you make three decisions."',
      '"Drop: the customer\'s greeting from turn 1, an old tool dump from turn 3, a duplicate product lookup — stale, redundant, low-signal. Summarize it or evict it."',
      '"Keep: the system prompt, the current task, the last few turns of reasoning. This is the spine — without it the agent loses coherence."',
      '"Add: the fresh context for THIS turn. The customer just asked about a specific winter jacket. You need the current price, the stock level, the customer\'s browsing history for the last hour. Those are the tokens that earn a seat right now."',
      '*** TRANSITION *** "That\'s the theory. Now let\'s look at the engineering. Four pillars."',
    ],
    render: Slide08,
  },
  {
    id: 9,
    section: 'Four Pillars',
    theme: 'white',
    notes: [
      '~60s · "Four pillars of context engineering. The first two — memory management and tool orchestration — live in the agent framework. LangGraph, ADK, CrewAI — that\'s where those get configured. You influence them, but you don\'t own them."',
      '"The green ones — pillars 3 and 4 — that\'s YOUR stack. Compaction and pruning: how to keep the window clean. Dynamic information flow: how to inject the right data just-in-time from your streams."',
      '"Pillar 4 is the heart of this talk. It\'s where Kafka meets the context window. And there are two fundamentally different philosophies for how to deliver it."',
    ],
    render: Slide09,
  },
  {
    id: 10,
    section: 'Eager vs. Lazy',
    theme: 'white',
    notes: [
      '~60s · "Two ways to deliver dynamic context. Think of it as Backpack versus Grocery List."',
      '"Eager — the backpack. A Flink job runs continuously, joining clicks, purchases, product catalog, inventory, and user profiles into pre-shaped context records. When the shopping assistant gets invoked, the context is already packed and waiting. One read, one record, everything the agent needs."',
      '"Lazy — the grocery list. The agent gets the raw click event and a list of what to fetch. It calls the product catalog API, queries the inventory state store, looks up the user profile — each one a separate round trip. More flexible, but every call costs tokens and wall-clock time."',
      '"Neither is universally better. We\'ll explore when each wins across three challenges: the join, the window, and the compaction."',
    ],
    render: Slide10,
  },
  {
    id: 11,
    section: 'Challenge #1',
    theme: 'white',
    notes: [
      '~60s · "This table bridges the agent demands we saw earlier to concrete infrastructure patterns. I want to highlight two rows."',
      '"Reasoning — bounded context window. The infrastructure pattern is backpressure and pruning at the data plane. The token budget is not just a model constraint — it\'s a flow control signal back to your pipeline. We\'ll come back to this."',
      '"Failure cost — cascading mesh failures. The infrastructure pattern is event sourcing. If our shopping assistant recommends a product based on stale inventory, and the order agent acts on that recommendation, you need to trace the failure back to the source event. That requires replay and audit — which Kafka gives you for free, if you design for it."',
      '*** TRANSITION *** "Three challenges ahead. Let\'s start with the hardest one: assembling context from multiple streams."',
    ],
    render: Slide11,
  },
  {
    id: 12,
    section: 'Challenge #1 · Context Assembly',
    theme: 'white',
    notes: [
      '~60s · "Here\'s our shopping assistant\'s data landscape. Five Kafka topics, each on its own clock and rate. Clicks at 5,000 per second. Purchases at 50. Product catalog is versioned — changes maybe once a day. Inventory at 100 per second. User profile is CDC — change data capture from a Postgres table."',
      '"The customer clicks on a winter jacket. The raw click event has a product_id and a user_id. That\'s it. The agent needs to know: what\'s the price? Is it in stock in their size? What\'s the customer\'s shipping zip? What have they bought before? Without enrichment, those fields are just missing. And the agent won\'t say \'I don\'t know\' — it will fill in the gaps with plausible-sounding fiction."',
      '"The assembler in the middle — join, rank, compress, tokenize — that\'s what we need to build. Let me show you two ways to do it."',
    ],
    render: Slide12,
  },
  {
    id: 13,
    section: 'Challenge #1 · Context Assembly',
    theme: 'white',
    notes: [
      '~90s · "Two code blocks. You can read them — let\'s walk through the trade-offs."',
      '"Lazy, on the left: the agent receives the raw click and makes five sequential state-store lookups. Product, user, inventory, sizing, returns. Each one is a round trip. The agent cannot reason until ALL five return. And each lookup costs tokens — the tool definition, the request, the response — roughly 200 tokens per call, times five."',
      '"Eager, on the right: one Flink SQL query does all five joins. The temporal join — FOR SYSTEM_TIME AS OF — aligns each dimension table to the click\'s event time, not wall-clock time. The result is a single pre-shaped record that lands on a Kafka topic. When the agent needs it, one read."',
      '"The key trade-off: Flink moves the join cost to the data plane, where it runs continuously and amortizes across every click event. The agent never sees the join complexity."',
    ],
    render: Slide13,
  },
  {
    id: 14,
    section: 'Challenge #1 · Context Assembly',
    theme: 'white',
    notes: [
      '~75s · "This is the Eager pipeline end-to-end. Left to right."',
      '"Five source topics feed into the Flink job. The temporal join aligns them by event time — that watermark at t=14:32:07 is the synchronization barrier. Nothing flows downstream until the barrier confirms all five streams have caught up to that timestamp."',
      '"On the right, the assembled context records roll off the join. Each one is a complete, pre-shaped payload — user_id, product, price, stock level, shipping zip, browsing history — ready for the agent. The animated feed shows records arriving every second. That\'s real-time context assembly."',
      '"The agent reads from this topic via MCP — Model Context Protocol. And in production, MCP isn\'t a static endpoint. Both Confluent and StreamNative implement a tool registry — when a new data source or agent is deployed, subscribing agents discover it automatically. The plumbing is self-updating."',
    ],
    render: Slide14,
  },
  {
    id: 15,
    section: 'Challenge #1 · Context Assembly',
    theme: 'white',
    notes: [
      '~75s · "Now the Lazy version of the same scenario. The agent is the orchestrator."',
      '"The shopping assistant receives the click event and fans out five parallel tool calls — stream tail for recent clicks, API call for the last purchase, KV lookup for the product catalog, tool call for inventory, CDC fetch for the user profile. Look at the latency numbers — the KV lookup returns in 12ms, but inventory takes 88ms."',
      '"The agent\'s total wait is the max, not the sum — 88ms — because the calls run in parallel. But each call costs tokens: tool definitions, request formatting, response parsing. Five calls times roughly 200 tokens each is a thousand tokens just for the plumbing. That\'s budget the Eager approach doesn\'t spend."',
      '"So why would anyone choose Lazy? Flexibility. The agent can decide at reasoning time WHICH lookups to make. We\'ll see where that matters later."',
    ],
    render: Slide15,
  },
  {
    id: 16,
    section: 'Challenge #2 · State Management',
    theme: 'white',
    notes: [
      '~45s · "Challenge two: state. A single click is noise. But five clicks on winter jackets in the last hour — that\'s intent."',
      '"Our shopping assistant needs to know: is this customer browsing casually, or are they about to buy? That signal lives in sequences, not single events. And agents are stateless by default — every inference starts with an empty window. We have to inject state."',
      '"There\'s also a second dimension of state: the agent\'s OWN memory. StreamNative\'s Orca externalizes every agent decision as an event on the log — persistent memory that survives across sessions and is replayable. The agent remembers that this customer returned their last winter jacket purchase, not because a pipeline enriched the context, but because the agent\'s prior reasoning is stored as events."',
      '"Two patterns ahead: windowing — how to summarize a time range — and compaction — how to prune what reaches the agent."',
    ],
    render: Slide16,
  },
  {
    id: 17,
    section: 'Challenge #2 · State Management',
    theme: 'white',
    notes: [
      '~60s · "Eager: the Flink hopping window on the left. It aggregates clicks into one-hour windows, advancing every five minutes. The output is a summary — total clicks, action history — that lands on a topic. When the shopping assistant is invoked, it reads one summary record. The intent signal is pre-computed."',
      '"Lazy: the agent range-reads the last hour of raw events from the state store and builds the summary itself. More data in the window, but the agent has the full sequence — every click, every product viewed, every comparison."',
      '"Trade-off: Eager saves tokens and latency. Lazy preserves raw detail. But Eager has a trap — and it\'s on the next slide."',
    ],
    render: Slide17,
  },
  {
    id: 18,
    section: 'Challenge #2 · State Management',
    theme: 'white',
    notes: [
      '~75s · "The lossy compression trap. This is a real production failure mode."',
      '"Our shopping assistant\'s customer made 1,000 clicks in the last hour. The eager window compresses that to click_count: 1000. Clean, cheap, four tokens. But the agent is now reasoning over a number when the truth was in the order."',
      '"The high-signal events were the LAST THREE clicks — all on the \'Compare\' button for the same winter jacket. That\'s purchase intent. But the hopping window compressed those three clicks into the same count as the 997 casual browses. The agent has the volume but lost the velocity."',
      '"Fix: tiered windowing. Keep the last 15 minutes of events raw — full detail, full sequence. Summarize 15 minutes to 1 hour. Aggregate 1 to 24 hours. The agent gets granularity where it matters and trends where it doesn\'t."',
    ],
    render: Slide18,
  },
  {
    id: 19,
    section: 'Challenge #2 · State Management',
    theme: 'white',
    notes: [
      '~60s · "Two flavors of pruning. The Flink SQL on the left is structural — filter out heartbeats, keep only the 3 most recent events per user-action pair. Noise never reaches the agent. For our shopping assistant, this means the 4,000 heartbeat events and page-load pings from the last hour get dropped in the pipeline. The agent never sees them."',
      '"The Python on the right is semantic — the agent itself decides what matters. Only SEARCH, ADD_TO_CART, and COMPARE events are relevant for THIS recommendation. More flexible — the agent can change its filter based on the task — but it costs tokens to receive and discard."',
      '"In production, you want both. Structural pruning in the pipeline to cut the obvious noise. Semantic pruning at reasoning time for task-specific relevance."',
    ],
    render: Slide19,
  },
  {
    id: 20,
    section: 'Challenge #3 · Observability',
    theme: 'white',
    notes: [
      '~45s · "Challenge three — and the hardest one. Is the context fit for the agent?"',
      '"Traditional observability tells you the pipeline is running — events are flowing, latency is within bounds, no errors. That\'s checking if the pipe is leaking. Agent-facing observability tells you if the water is drinkable."',
      '"Our shopping assistant can receive a context payload that\'s schema-valid, on-time, and complete by every traditional metric — and still recommend a sold-out product because the inventory join lagged by 90 seconds. Your existing dashboards won\'t catch it."',
      '"So the agent should write its decisions back to the same event bus it reads from. Every inference becomes a first-class event — auditable, queryable, replayable. This is persistent streaming memory, and it closes the observability loop that traditional monitoring can\'t reach. We\'ll see exactly how in a few slides."',
    ],
    render: Slide20,
  },
  {
    id: 21,
    section: 'Challenge #3 · Observability',
    theme: 'white',
    notes: [
      '~60s · "Traditional data quality has three pillars: completeness, validity, volume. They check the SHAPE of the data, not its FITNESS as context."',
      '"Completeness: user_segment is not null — passes. But the windowing job lagged and only 10 of the customer\'s 50 sessions made it into the window. The field is present but the data is incomplete for reasoning."',
      '"Volume: a spike in heartbeat events triggers a volume alert. But heartbeats are noise for the shopping assistant — the real problem is that they\'re crowding out the click and purchase events that carry signal. That\'s not a volume problem, it\'s a signal-to-noise problem."',
      '"We need metrics at the assembled context level — fitness for reasoning, not fitness for storage."',
    ],
    render: Slide21,
  },
  {
    id: 22,
    section: 'Challenge #3 · Observability',
    theme: 'white',
    notes: [
      '~90s · "Four new metrics. These are the core takeaway for this section."',
      '"Token Efficiency — signal-to-noise. What percentage of the context tokens did the shopping assistant actually cite or use in its response? SLO: above 60%. Below that, you\'re burning tokens on noise the agent ignores."',
      '"Semantic Drift — summary fidelity. Remember the hopping window from the state management section? Is that summary still faithful to the raw events? Embed both, measure cosine distance. If drift exceeds 0.15, the summary has diverged from reality."',
      '"Context Staleness — world-state latency. The delta between when the source event happened and when it appears in the assembled context. P99 under 500ms. And coherence skew — all five sources in the join should agree on \'now\' within 5 seconds."',
      '"Reference Integrity — entity consistency. When the shopping assistant cites product SKU-AX9, does that SKU actually exist in the product catalog topic right now? Above 99.5% or you have hallucination risk from stale references."',
    ],
    render: Slide22,
  },
  {
    id: 23,
    section: 'Challenge #3 · Observability',
    theme: 'white',
    notes: [
      '~60s · "Where does each metric live? This is the practical guidance."',
      '"Token Efficiency is post-inference — you can\'t measure it until the agent responds. An async job reads the context debug log, compares assembled chunks against the agent\'s output using embedding similarity, and flags unused tokens."',
      '"Semantic Drift is a periodic audit — hourly, sample N summaries, retrieve the raw events they were built from, embed both, compute distance."',
      '"Context Staleness runs on the Flink join itself — real-time. Every assembled record carries timestamps from all sources. Freshness = now minus the oldest source timestamp."',
      '"Reference Integrity has two parts: assembly-time — a counter on the Flink join tracking LEFT JOIN nulls — and post-inference — an async consumer on the agent\'s actions topic that back-checks cited entity IDs against the source-of-truth topics."',
    ],
    render: Slide23,
  },
  {
    id: 24,
    section: 'Challenge #3 · Observability',
    theme: 'white',
    notes: [
      '~60s · "If you build one thing from this section, build this. A context debug log."',
      '"Every time the shopping assistant runs an inference, log the full assembled prompt, every source that contributed, the per-source latency, and the citations the agent returned. This JSON is about 5KB per inference."',
      '"Look at the latencies: clicks_stream at 38ms, product_catalog at 12ms, user_profile at 18ms, inventory at 88ms. That 88ms is your laggard — we\'ll name that failure mode in a moment."',
      '"The mindset shift: in traditional data, you log the query. In agentic systems, you log the assembled context. The query is just a prompt. The context is the full picture the agent reasoned over."',
      '"And because every input is an event on the log, you can go further than logging — you can REPLAY. The shopping assistant recommended a sold-out item at 2 AM. Replay the exact event sequence through the agent and see what it saw. That\'s not a log query — it\'s a deterministic reproduction of the failure."',
      '"This is persistent streaming memory. The agent writes its decisions back to the event bus as first-class events. Now you have a complete, replayable record — not just what the agent saw, but what it decided and why. Orca calls this out explicitly: every agent decision is an event, and event replay gives you deterministic debugging for free."',
    ],
    render: Slide24,
  },
  {
    id: 25,
    section: 'Failure Modes',
    theme: 'white',
    notes: [
      '~75s · "Three failure modes worth naming, because named things are recognizable in production."',
      '"Temporal Tearing: clicks_stream runs at 5K/sec. user_profile CDC updates every few minutes. A purchase event hits the Flink join before the user profile has caught up. The join emits a purchase for a user who \'doesn\'t exist\' in the profile table yet. Fix: interval joins or temporal tables on the slow side — give the slow stream a grace period."',
      '"Context Echo: the shopping assistant issues a 10% discount code. That action becomes an event in the agent\'s input stream. Next inference, it sees the discount and issues another one. Ten discount codes in five minutes. Fix: source filtering on actor_id — exclude events the agent itself generated."',
      '"The Laggard Problem: in the Eager approach, your freshness equals the SLOWEST stream plus your watermark slack. Inventory at 88ms is the bottleneck for the entire join. Don\'t tune for speed — tune for slack. Pick your watermark deliberately."',
    ],
    render: Slide25,
  },
  {
    id: 26,
    section: 'Advanced Patterns',
    theme: 'white',
    notes: [
      '~60s · "Remember the token budget from slide 7? It\'s not just a limit — it\'s a backpressure signal from the agent back to your pipeline."',
      '"Our shopping assistant has a 200K token window. The conversation is on turn 47. Static content plus history is consuming 140K tokens. That leaves 60K for fresh context. But the eager join is producing context records that are 80K tokens — too big. Something gets cut."',
      '"If your pipeline doesn\'t know the budget, the truncation is random. Random truncation equals random agent behavior. Three strategies — choose in the pipeline, not the prompt. Truncate: drop lowest-priority sources — maybe the 24-hour aggregate goes first. Summarize: compress the browsing history mid-stream. Defer: hold the cross-sell recommendations in a sidecar topic until the agent explicitly asks for them."',
    ],
    render: Slide26,
  },
  {
    id: 27,
    section: 'Delivery Models',
    theme: 'white',
    notes: [
      '~75s · "Three ways to deliver context. Not two — three. This is the spectrum your architecture sits on."',
      '"Push — the Flink job continuously joins and writes pre-assembled records to a topic. The shopping assistant reads one record per click. Five milliseconds. This is what Confluent\'s Real-Time Context Engine does at GA — and it\'s the Eager pattern we\'ve been building."',
      '"Query — the agent queries a materialized live table at inference time. Not a full pre-joined record — a filtered, ranged, projected slice. \'Give me this user\'s purchases in the outerwear category from the last 30 days.\' The data is continuously materialized by the streaming platform, but the agent reads flexibly. Fifteen milliseconds, no external database."',
      '"Pull — the agent fans out to external APIs and state stores. Weather, competitor pricing, third-party reviews. Each call is a round trip. A hundred and twenty milliseconds for three parallel calls. This is Lazy — maximum flexibility, highest cost."',
      '"Most production systems use all three. The next slide shows you how."',
    ],
    render: Slide27,
  },
  {
    id: 28,
    section: 'Hybrid Architecture',
    theme: 'white',
    notes: [
      '~45s · "Our shopping assistant has been working beautifully with the Eager pipeline. Then the customer asks: \'should I bring a jacket when I pick this up?\' We need real-time weather."',
      '"Weather isn\'t in any of our five topics. And we can\'t pre-join it — the cardinality is unbounded. Every user has a different location. The weather changes minute by minute. Any pre-computed value is immediately stale. We\'ve hit Eager\'s ceiling."',
      '"This is where Pull wins: a single on-demand tool call to a weather API, scoped to this user\'s shipping zip, right now. And for the flexible middle — purchase history filtered by category — that\'s a Query against a live table."',
    ],
    render: Slide28,
  },
  {
    id: 29,
    section: 'Hybrid Architecture',
    theme: 'white',
    notes: [
      '~75s · "The production answer is Hybrid — and now you have three tools, not two."',
      '"Push lane — the critical five. User profile, tier, current product, inventory, browsing summary. These are predictable, high-frequency, and bounded. Pre-join them in Flink. Have them waiting on a topic. Five milliseconds."',
      '"Query lane — flexible enrichment. The customer is browsing outerwear. You query the live table for their purchase history filtered to outerwear, their return history in the last 90 days, their size preferences. The data is materialized continuously, but the agent reads the slice it needs. Fifteen milliseconds, no external database round trip."',
      '"Pull lane — the long tail. Weather, competitor pricing, external reviews, cross-sell recommendations. Unpredictable, volatile, or outside your domain. Fetch on demand via tool calls."',
      '"The exercise: identify YOUR critical five for Push. What flexible enrichments fit Query. Everything else goes Pull. That\'s your hybrid architecture."',
      '*** NOTE *** Both Confluent\'s RTCE and StreamNative\'s Orca support this hybrid model — the implementation details differ but the pattern is the same.',
    ],
    render: Slide29,
  },
  {
    id: 30,
    section: 'Call to Action',
    theme: 'white',
    notes: [
      '~75s · Read each question aloud. Pause after each to let the audience self-assess.',
      '"One: can you deliver an enriched context payload in under 100ms? For our shopping assistant, that\'s the full five-topic join from click to assembled record."',
      '"Two: can you summarize event histories, not just store them? That\'s the tiered windowing we built to avoid the lossy compression trap."',
      '"Three: can you measure signal-to-noise on assembled contexts? Not topic-level metrics — context-level fitness."',
      '"Four: can you route events by intent, not just by topic? The difference between a casual browse and a purchase-intent click — can your pipeline tell them apart?"',
      '"Five: can you trace a single inference back to every source event that contributed? That\'s the context debug log."',
      '"Four out of five means you\'re ready. Less than three — start with observability. You can\'t improve what you can\'t see."',
    ],
    render: Slide30,
  },
  {
    id: 31,
    section: 'Closing',
    theme: 'white',
    notes: [
      '~45s · Slow down. One line at a time.',
      '"In traditional software, the database is the source of truth."',
      '"In agentic systems — as we saw with our shopping assistant — the context window is the source of truth."',
      '"Your job, whether you\'re writing Flink SQL or wiring up an Orca agent, isn\'t to move data anymore. It\'s to curate reality for a reasoning engine."',
      '*** PAUSE *** "Thank you." *** WAIT *** for applause before taking questions.',
    ],
    render: Slide31,
  },
];
